import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Divider,
  Progress,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Tabs,
  Tab,
  Tooltip,
  Autocomplete,
  AutocompleteItem,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { SEASONS, VALID_FORMATS } from 'common/src/utils/definitions';
import {
  Species,
  Market,
  Ship,
  ShipTrip,
  ExporterReceiver,
  ReceiverMapping,
} from 'common/src/utils/types';
import { excelDateToJSDate, formatDate } from '../../utils/functions';
import { parse as parseDate } from 'date-fns';
import { calculateSeasonFromDate } from '../../utils/seasonUtils';
import { getMarkets } from 'common/src/api/markets';
import { getShips } from 'common/src/api/ships';
import { getShipTrips } from 'common/src/api/ship_trips';
import { getSettlementNoDetailsByExporterId } from 'common/src/api/settlements';
import {
  getExporterDivisions,
  type ExporterDivision,
} from 'common/src/api/exporter-divisions';
import { normalizeShipmentReferenceKey } from 'common/src/api/shipments';
import { supabase } from 'common/src/supabase';
import {
  addReceiverToExporter,
  getReceiversByExporter,
} from 'common/src/api/exporterReceivers';
import { getFruticReceiverMapping } from 'common/src/api/frutic';
import TransportTypeSelector from '../common/TransportTypeSelector';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface GeneralCostosRow extends Record<string, any> {}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface IngresosRow extends Record<string, any> {}

interface ValidationError {
  row: number;
  field: string;
  message: string;
  sheet: 'General y Costos' | 'Ingresos';
}

interface ProcessedDataType {
  generalCostos: GeneralCostosRow[];
  ingresos: IngresosRow[];
  validationErrors: ValidationError[];
  validationWarnings: ValidationError[];
  invalidReferences: string[]; // Add this
  summary: {
    totalGeneralRows: number;
    totalIngresosRows: number;
    validGeneralRows: number;
    validIngresosRows: number;
    hasErrors: boolean;
  };
}

interface ExcelMassiveUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const trimRowKeys = <T extends Record<string, unknown>>(
  row: T
): Record<string, unknown> => {
  const trimmedRow: Record<string, unknown> = {};
  Object.entries(row).forEach(([key, value]) => {
    trimmedRow[key.trim()] = value;
  });
  return trimmedRow;
};

/** date-fns format strings keyed by user-facing label */
const DATE_FORMAT_OPTIONS: { label: string; value: string }[] = [
  { label: 'DD/MM/YYYY', value: 'dd-MM-yyyy' },
  { label: 'MM/DD/YYYY', value: 'MM-dd-yyyy' },
  { label: 'YYYY-MM-DD', value: 'yyyy-MM-dd' },
];

const ALL_DATE_FORMATS = [
  'dd-MM-yyyy',
  'yyyy-MM-dd',
  'MM-dd-yyyy',
  'dd-MM-yy',
  'MM-dd-yy',
  'yy-MM-dd',
];

const normalizeReceiverName = (value: string | null | undefined) =>
  (value || '').trim().toUpperCase();

const CREATE_RECEIVER_OPTION_KEY = '__create_new_receiver__';

/**
 * Parse a date value from Excel (numeric serial or string) into an ISO-like
 * string "YYYY-MM-DDThh:mm:ss" that the rest of the codebase expects.
 *
 * @param preferredFormat  date-fns format string chosen by the user (e.g. "dd-MM-yyyy").
 *                         Tried first; remaining formats used as fallback.
 * Returns null when the value cannot be parsed.
 */
const parseDateValue = (
  value: unknown,
  preferredFormat?: string
): string | null => {
  // Excel serial number — unambiguous, ignore preferredFormat
  if (typeof value === 'number') {
    const isoLocal = excelDateToJSDate(value);
    const asDate = new Date(isoLocal);
    if (!isNaN(asDate.getTime())) return isoLocal;
    return null;
  }

  if (typeof value === 'string') {
    const dateOnlyStr = value.split(' ')[0].trim();
    // Normalize . and / separators to -
    const normalizedValue = dateOnlyStr.replace(/[./]/g, '-');

    const tryParseWithFormat = (fmt: string): Date | null => {
      try {
        const d = parseDate(normalizedValue, fmt, new Date());
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          if (year < 100) {
            d.setFullYear(year < 50 ? 2000 + year : 1900 + year);
          }
          return d;
        }
      } catch {
        // ignore
      }
      return null;
    };

    const toIso = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}T00:00:00`;
    };

    // Try preferred format first
    if (preferredFormat) {
      const parsed = tryParseWithFormat(preferredFormat);
      if (parsed) return toIso(parsed);
    }

    // Fallback to remaining formats
    for (const fmt of ALL_DATE_FORMATS) {
      if (fmt === preferredFormat) continue;
      const parsed = tryParseWithFormat(fmt);
      if (parsed) return toIso(parsed);
    }
    return null;
  }

  return null;
};

const ExcelMassiveUploadModal: React.FC<ExcelMassiveUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess, // eslint-disable-line @typescript-eslint/no-unused-vars
}) => {
  const { selectedSeason: globalSeason } = useFilterStore();
  const { species, selectedSpecies: selectedSpeciesFromStore } = useDataStore();
  const { userProfile, session } = useUserStore();

  const [season, setSeason] = useState<string>(globalSeason || '');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(
    selectedSpeciesFromStore ?? null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dateFormat, setDateFormat] = useState<string>('dd-MM-yyyy');
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    processed: 0,
    total: 0,
  });
  const [uploadStage, setUploadStage] = useState<
    'settlements' | 'comparison' | null
  >(null);
  const [processedData, setProcessedData] = useState<ProcessedDataType | null>(
    null
  );
  const [step, setStep] = useState<'upload' | 'review'>('upload');
  const [markets, setMarkets] = useState<Market[]>([]);
  const [invalidMarkets, setInvalidMarkets] = useState<Record<number, string>>(
    {}
  );
  const [editingMarket, setEditingMarket] = useState<number | null>(null);
  const [invalidDestinationPorts, setInvalidDestinationPorts] = useState<
    Record<number, string>
  >({});
  const [editingDestinationPort, setEditingDestinationPort] = useState<
    number | null
  >(null);
  const [editingETD, setEditingETD] = useState<number | null>(null);
  const [editingETA, setEditingETA] = useState<number | null>(null);
  const [editingFechaVenta, setEditingFechaVenta] = useState<number | null>(
    null
  );
  const [invalidComparableMarkets, setInvalidComparableMarkets] = useState<
    Record<number, string>
  >({});
  const [editingComparableMarket, setEditingComparableMarket] = useState<
    number | null
  >(null);
  const [ships, setShips] = useState<Ship[]>([]);
  const [shipTrips, setShipTrips] = useState<ShipTrip[]>([]);
  const [invalidTransports, setInvalidTransports] = useState<
    Record<number, string>
  >({});
  const [editingShip, setEditingShip] = useState<number | null>(null);
  const [editingTrip, setEditingTrip] = useState<number | null>(null);
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  const [settlementValidationResults, setSettlementValidationResults] =
    useState<
      Record<
        string,
        {
          exists: boolean;
          isBlocked?: boolean;
          settlementId?: number;
          divisionId?: number | null;
        }
      >
    >({});
  const [isValidatingSettlements, setIsValidatingSettlements] = useState(false);
  // Business units of the exporter; the "División" column only matters when there are several.
  const [divisions, setDivisions] = useState<ExporterDivision[]>([]);
  const [uploadFailures, setUploadFailures] = useState<
    Array<{ reference: string; error: string }>
  >([]);
  // Add state after invalidTransports
  const [invalidShips, setInvalidShips] = useState<Record<number, string>>({});
  // Add state after invalidShips
  const [invalidShipTrips, setInvalidShipTrips] = useState<
    Record<number, string>
  >({});
  const [exporterReceivers, setExporterReceivers] = useState<
    ExporterReceiver[]
  >([]);
  const [receiverMappings, setReceiverMappings] = useState<ReceiverMapping[]>(
    []
  );
  const [receiverMappingSelections, setReceiverMappingSelections] = useState<
    Record<string, string>
  >({});
  const [receiverSearchValues, setReceiverSearchValues] = useState<
    Record<string, string>
  >({});
  const [receiverAutocompleteOpenKeys, setReceiverAutocompleteOpenKeys] =
    useState<Record<string, boolean>>({});
  const [hiddenReceiverAutocompleteKey, setHiddenReceiverAutocompleteKey] =
    useState<string | null>(null);
  const [createReceiverGroup, setCreateReceiverGroup] = useState<{
    key: string;
    receiverName: string;
  } | null>(null);
  const [createReceiverName, setCreateReceiverName] = useState('');
  const [isCreatingReceiver, setIsCreatingReceiver] = useState(false);
  const [isLoadingReceivers, setIsLoadingReceivers] = useState(false);
  const closingReceiverAutocompleteKeyRef = React.useRef<string | null>(null);
  const createReceiverTimeoutRef = React.useRef<ReturnType<
    typeof window.setTimeout
  > | null>(null);

  const uploadProgressPercent =
    uploadProgress.total > 0
      ? Math.round((uploadProgress.processed / uploadProgress.total) * 100)
      : 0;

  useEffect(() => {
    if (!season && globalSeason) {
      setSeason(globalSeason);
    }
  }, [globalSeason, season]);

  useEffect(() => {
    if (!selectedSpecies && selectedSpeciesFromStore) {
      setSelectedSpecies(selectedSpeciesFromStore);
    }
  }, [selectedSpeciesFromStore, selectedSpecies]);

  useEffect(() => {
    return () => {
      if (createReceiverTimeoutRef.current) {
        window.clearTimeout(createReceiverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !userProfile?.exporter_id) {
      setExporterReceivers([]);
      setReceiverMappings([]);
      return;
    }

    let isMounted = true;
    const fetchReceivers = async () => {
      setIsLoadingReceivers(true);
      try {
        const [receiversData, mappingsData] = await Promise.all([
          getReceiversByExporter(userProfile.exporter_id),
          getFruticReceiverMapping({
            exporter_id: userProfile.exporter_id,
          }),
        ]);

        if (!isMounted) return;

        setExporterReceivers(
          [...(receiversData || [])].sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        );
        setReceiverMappings(
          ((mappingsData || []) as ReceiverMapping[]).filter(
            (mapping) => mapping.exporter_receiver_id
          )
        );
      } catch (error) {
        console.error('Error fetching exporter receivers:', error);
        toast.error('Error al cargar clientes oficiales');
      } finally {
        if (isMounted) {
          setIsLoadingReceivers(false);
        }
      }
    };

    fetchReceivers();

    return () => {
      isMounted = false;
    };
  }, [isOpen, userProfile?.exporter_id]);

  // Fetch markets, ships, and ship trips on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [marketsData, shipsData, shipTripsData] = await Promise.all([
          getMarkets(),
          getShips(),
          getShipTrips(),
        ]);
        setMarkets(marketsData);
        setShips(shipsData);
        setShipTrips(shipTripsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error al cargar los datos');
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!userProfile?.exporter_id) return;
    getExporterDivisions(userProfile.exporter_id)
      .then(setDivisions)
      .catch((error) => console.error('Error loading divisions:', error));
  }, [userProfile?.exporter_id]);

  // Division each reference belongs to: the "División" column when present,
  // else the only shipment with that reference, else the default division.
  // A reference shared by several divisions must name one.
  const resolveReferenceDivisions = async (
    references: string[],
    generalRows: GeneralCostosRow[],
    errors: ValidationError[]
  ): Promise<Record<string, number | null>> => {
    const result: Record<string, number | null> = {};
    if (!userProfile?.exporter_id || !selectedSpecies?.id || !season) {
      return result;
    }
    const defaultDivisionId = divisions.find((d) => d.is_default)?.id ?? null;
    const divisionIdByName = new Map(
      divisions.map((d) => [d.name.trim().toLowerCase(), d.id])
    );
    const rowsOf = (reference: string) =>
      generalRows
        .map((row, index) => ({ row, index }))
        .filter(
          ({ row }) =>
            String(row['Número de referencia'] || '')
              .trim()
              .toUpperCase() === reference
        );
    const pending: string[] = [];
    references.forEach((reference) => {
      const explicit = String(rowsOf(reference)[0]?.row['División'] || '')
        .trim()
        .toLowerCase();
      const explicitId = explicit ? divisionIdByName.get(explicit) : undefined;
      if (explicitId) result[reference] = explicitId;
      // An unknown name is already reported on the row; do not resolve it.
      else if (explicit) result[reference] = null;
      else pending.push(reference);
    });
    if (pending.length === 0 || divisions.length <= 1) {
      pending.forEach((reference) => {
        result[reference] = defaultDivisionId;
      });
      return result;
    }
    const { data, error } = await supabase
      .from('shipments')
      .select('reference_key, division_id')
      .eq('exporter_id', userProfile.exporter_id)
      .eq('season', season)
      .eq('species_id', selectedSpecies.id)
      .is('source_deleted_at', null)
      .in('reference_key', pending.map((r) => normalizeShipmentReferenceKey(r)));
    if (error) throw error;
    const divisionsByKey = new Map<string, Set<number>>();
    (data || []).forEach((row) => {
      const set = divisionsByKey.get(row.reference_key) ?? new Set<number>();
      set.add(Number(row.division_id));
      divisionsByKey.set(row.reference_key, set);
    });
    const divisionNames = divisions.map((d) => d.name).join(', ');
    pending.forEach((reference) => {
      const found = divisionsByKey.get(normalizeShipmentReferenceKey(reference));
      if (found && found.size > 1) {
        result[reference] = null;
        rowsOf(reference).forEach(({ index }) => {
          errors.push({
            row: index + 2,
            field: 'División',
            message: `La referencia existe en ${found.size} divisiones; indica la columna "División" (${divisionNames})`,
            sheet: 'General y Costos',
          });
        });
      } else {
        result[reference] = found ? Array.from(found)[0] : defaultDivisionId;
      }
    });
    return result;
  };

  const getReceiverResolution = (
    receiverName: string | null | undefined
  ): {
    receiver: ExporterReceiver | null;
    source: 'official' | 'mapping' | 'manual' | null;
  } => {
    const normalizedName = normalizeReceiverName(receiverName);
    if (!normalizedName) {
      return { receiver: null, source: null };
    }

    const officialReceiver = exporterReceivers.find(
      (receiver) => normalizeReceiverName(receiver.name) === normalizedName
    );
    if (officialReceiver) {
      return { receiver: officialReceiver, source: 'official' };
    }

    const existingMapping = receiverMappings.find(
      (mapping) =>
        normalizeReceiverName(mapping.frutic_receiver_name) === normalizedName
    );
    if (existingMapping) {
      const mappedReceiver =
        existingMapping.exporter_receivers ||
        exporterReceivers.find(
          (receiver) => receiver.id === existingMapping.exporter_receiver_id
        ) ||
        null;

      if (mappedReceiver) {
        return { receiver: mappedReceiver, source: 'mapping' };
      }
    }

    const selectedReceiverId = receiverMappingSelections[normalizedName];
    const selectedReceiver = exporterReceivers.find(
      (receiver) => receiver.id.toString() === selectedReceiverId
    );

    return selectedReceiver
      ? { receiver: selectedReceiver, source: 'manual' }
      : { receiver: null, source: null };
  };

  const hasUploadBlockingErrors = (
    generalRow: GeneralCostosRow,
    rowIndex: number
  ) => {
    if (!processedData) return true;

    const rowNumber = rowIndex + 2;
    const reference = String(generalRow['Número de referencia'])
      .trim()
      .toUpperCase();

    const hasGeneralErrors = processedData.validationErrors.some(
      (error) => error.sheet === 'General y Costos' && error.row === rowNumber
    );

    const hasIngresosErrors = processedData.validationErrors.some((error) => {
      if (error.sheet !== 'Ingresos') return false;

      const ingresosRowIndex = error.row - 2;
      const ingresosRow = processedData.ingresos[ingresosRowIndex];
      if (!ingresosRow) return false;

      const ingresosReference = String(
        ingresosRow['Número de Referencia'] || ''
      )
        .trim()
        .toUpperCase();

      return ingresosReference === reference;
    });

    return hasGeneralErrors || hasIngresosErrors;
  };

  const getValidGeneralRowsForUpload = () => {
    if (!processedData) return [];

    return processedData.generalCostos.filter(
      (generalRow, rowIndex) =>
        !hasUploadBlockingErrors(generalRow, rowIndex)
    );
  };

  const unresolvedReceiverGroups = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; receiverName: string; count: number; references: string[] }
    >();

    getValidGeneralRowsForUpload().forEach((row) => {
      const receiverName = String(row['Cliente'] || '').trim();
      const key = normalizeReceiverName(receiverName);
      if (!key || getReceiverResolution(receiverName).receiver) return;

      const reference = String(row['Número de referencia'] || '')
        .trim()
        .toUpperCase();
      const existing = groups.get(key);

      if (existing) {
        existing.count += 1;
        if (reference) existing.references.push(reference);
        return;
      }

      groups.set(key, {
        key,
        receiverName,
        count: 1,
        references: reference ? [reference] : [],
      });
    });

    return Array.from(groups.values()).sort((a, b) =>
      a.receiverName.localeCompare(b.receiverName)
    );
  }, [
    processedData,
    exporterReceivers,
    receiverMappings,
    receiverMappingSelections,
  ]);

  const unresolvedReceiverCount = unresolvedReceiverGroups.reduce(
    (total, group) => total + group.count,
    0
  );

  const openCreateReceiverModal = (
    group: (typeof unresolvedReceiverGroups)[number]
  ) => {
    const prefilledName = (
      receiverSearchValues[group.key] || group.receiverName
    ).trim();
    closingReceiverAutocompleteKeyRef.current = group.key;
    setHiddenReceiverAutocompleteKey(group.key);
    setReceiverAutocompleteOpenKeys((prev) => ({
      ...prev,
      [group.key]: false,
    }));
    setCreateReceiverName(prefilledName);

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (createReceiverTimeoutRef.current) {
      window.clearTimeout(createReceiverTimeoutRef.current);
    }

    createReceiverTimeoutRef.current = window.setTimeout(() => {
      setCreateReceiverGroup({
        key: group.key,
        receiverName: group.receiverName,
      });
    }, 50);
  };

  const closeCreateReceiverModal = () => {
    closingReceiverAutocompleteKeyRef.current = null;
    setHiddenReceiverAutocompleteKey(null);
    if (createReceiverTimeoutRef.current) {
      window.clearTimeout(createReceiverTimeoutRef.current);
      createReceiverTimeoutRef.current = null;
    }
    setCreateReceiverGroup(null);
    setCreateReceiverName('');
    setIsCreatingReceiver(false);
  };

  const handleCreateReceiver = async () => {
    if (!userProfile?.exporter_id || !createReceiverGroup) return;

    const receiverName = createReceiverName.trim();
    if (!receiverName) {
      toast.error('Ingresa un nombre de cliente');
      return;
    }

    const existingReceiver = exporterReceivers.find(
      (receiver) =>
        normalizeReceiverName(receiver.name) ===
        normalizeReceiverName(receiverName)
    );

    if (existingReceiver) {
      setReceiverMappingSelections((prev) => ({
        ...prev,
        [createReceiverGroup.key]: existingReceiver.id.toString(),
      }));
      closeCreateReceiverModal();
      toast.info('El cliente ya existe y fue seleccionado');
      return;
    }

    setIsCreatingReceiver(true);
    try {
      const createdReceiver = await addReceiverToExporter(
        userProfile.exporter_id,
        receiverName
      );

      setExporterReceivers((prev) =>
        [...prev, createdReceiver].sort((a, b) => a.name.localeCompare(b.name))
      );
      setReceiverMappingSelections((prev) => ({
        ...prev,
        [createReceiverGroup.key]: createdReceiver.id.toString(),
      }));
      closeCreateReceiverModal();
      toast.success('Cliente creado y seleccionado');
    } catch (error) {
      console.error('Error creating receiver:', error);
      toast.error('Error al crear cliente');
    } finally {
      setIsCreatingReceiver(false);
    }
  };

  const generateExcelTemplate = () => {
    if (!season || !selectedSpecies) {
      toast.error('Por favor selecciona temporada y especie');
      return;
    }

    try {
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Sheet 1: General y Costos
      const generalData = [
        {
          'Número de referencia': '',
          División: '',
          'Fecha de venta': '',
          Cliente: '',
          'Tipo de Negocio': '',
          'Orden de Venta': '',
          'Número de Contenedor': '',
          'Mercado Venta': '',
          'Mercado Comparable': '',
          'Moneda Local': '',
          'Incoterm Original': '',
          'Comisión (Moneda Local)': '',
          'Impuestos (Moneda Local)': '',
          'Repacking (Moneda Local)': '',
          'Otros Costos (Moneda Local)': '',
          'Tipo de Cambio': '',
          'Flete (USD)': '',
          'Tipo Transporte': '',
          'Nombre Nave': '',
          'Puerto de Destino': '',
          ETD: '',
          ETA: '',
        },
      ];

      const wsGeneral = XLSX.utils.json_to_sheet(generalData);

      // Set column widths for General sheet
      wsGeneral['!cols'] = [
        { wch: 20 }, // Número de referencia
        { wch: 15 }, // División
        { wch: 15 }, // Fecha de venta
        { wch: 20 }, // Cliente
        { wch: 15 }, // Tipo de Negocio
        { wch: 15 }, // Orden de Venta
        { wch: 18 }, // Número de Contenedor
        { wch: 15 }, // Mercado Venta
        { wch: 18 }, // Mercado Comparable
        { wch: 12 }, // Moneda Local
        { wch: 15 }, // Incoterm Original
        { wch: 12 }, // Comisión (Moneda Local)
        { wch: 12 }, // Impuestos (Moneda Local)
        { wch: 12 }, // Repacking (Moneda Local)
        { wch: 12 }, // Otros Costos (Moneda Local)
        { wch: 15 }, // Tipo de Cambio
        { wch: 12 }, // Flete (USD)
        { wch: 15 }, // Tipo Transporte
        { wch: 20 }, // Nombre Nave
        { wch: 18 }, // Puerto de Destino
        { wch: 15 }, // ETD
        { wch: 15 }, // ETA
      ];

      XLSX.utils.book_append_sheet(wb, wsGeneral, 'General y Costos');

      // Sheet 2: Ingresos
      const ingresosData = [
        {
          'Número de Referencia': '',
          Pallet: '',
          Variedad: '',
          Formato: '',
          Calibre: '',
          Color: '',
          Etiqueta: '',
          Categoría: '',
          'Peso Bulto': '',
          'Cantidad de Bultos': '',
          'Precio Bulto (Moneda Local)': '',
        },
      ];

      const wsIngresos = XLSX.utils.json_to_sheet(ingresosData);

      // Set column widths for Ingresos sheet
      wsIngresos['!cols'] = [
        { wch: 20 }, // Número de Referencia
        { wch: 10 }, // Pallet
        { wch: 15 }, // Variedad
        { wch: 15 }, // Formato
        { wch: 12 }, // Calibre
        { wch: 12 }, // Color
        { wch: 15 }, // Etiqueta
        { wch: 12 }, // Categoría
        { wch: 12 }, // Peso Bulto
        { wch: 18 }, // Cantidad de Bultos
        { wch: 15 }, // Precio Bulto (Moneda Local)
      ];

      XLSX.utils.book_append_sheet(wb, wsIngresos, 'Ingresos');

      // Download the file
      const speciesNameNoEmoji = selectedSpecies.name
        .replace(/^[^\p{L}\p{N}]+/u, '')
        .trim();
      const fileName = `Plantilla_Liquidaciones_${speciesNameNoEmoji}_${season}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success('Plantilla descargada exitosamente');
    } catch (error) {
      console.error('Error generating Excel template:', error);
      toast.error('Error al generar la plantilla');
    }
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (!validTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos Excel (.xls, .xlsx)');
      return;
    }

    setSelectedFile(file);
    setReceiverMappingSelections({});
    setReceiverSearchValues({});
    setReceiverAutocompleteOpenKeys({});
    setHiddenReceiverAutocompleteKey(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const validateAndProcessExcel = async (
    file: File
  ): Promise<ProcessedDataType> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          // Check required sheets
          if (
            !workbook.SheetNames.includes('General y Costos') ||
            !workbook.SheetNames.includes('Ingresos')
          ) {
            reject(
              new Error(
                'El archivo debe contener las hojas "General y Costos" e "Ingresos"'
              )
            );
            return;
          }

          // Parse sheets
          const generalSheet = workbook.Sheets['General y Costos'];
          const ingresosSheet = workbook.Sheets['Ingresos'];

          const generalData = XLSX.utils
            .sheet_to_json(generalSheet)
            .map(trimRowKeys) as GeneralCostosRow[];
          const ingresosData = XLSX.utils
            .sheet_to_json(ingresosSheet)
            .map(trimRowKeys) as IngresosRow[];

          const validationErrors: ValidationError[] = [];
          const validationWarnings: ValidationError[] = [];
          const validFormatos = [...VALID_FORMATS];

          // Validate and clean General y Costos data
          const processedGeneralData = generalData.map((row, index) => {
            const rowNumber = index + 2; // Excel rows start at 2 (after header)

            // Clean and validate string fields
            const cleanedRow = { ...row };

            // Process string fields - trim and uppercase
            const stringFields = [
              'Número de referencia',
              'Cliente',
              'Tipo de Negocio',
              'Orden de Venta',
              'Número de Contenedor',
              'Mercado Venta',
              'Mercado Comparable',
              'Puerto de Destino',
              'Moneda Local',
              'Incoterm Original',
            ];
            stringFields.forEach((field) => {
              if (cleanedRow[field as keyof GeneralCostosRow]) {
                cleanedRow[field as keyof GeneralCostosRow] = String(
                  cleanedRow[field as keyof GeneralCostosRow]
                )
                  .trim()
                  .toUpperCase();
              }
            });

            // Automatically set Tipo de Cambio to 1 if Moneda Local is USD
            if (cleanedRow['Moneda Local'] === 'USD') {
              cleanedRow['Tipo de Cambio'] = 1;
            }

            // Validate mandatory fields
            const mandatoryFields = [
              'Número de referencia',
              'Fecha de venta',
              'Cliente',
              'Número de Contenedor',
              'Mercado Venta',
              'Puerto de Destino',
              'Moneda Local',
              'Incoterm Original',
              'Tipo de Cambio',
            ];
            mandatoryFields.forEach((field) => {
              const value = cleanedRow[field as keyof GeneralCostosRow];
              if (
                !value ||
                (typeof value === 'string' && value.trim() === '')
              ) {
                validationErrors.push({
                  row: rowNumber,
                  field,
                  message: 'Campo obligatorio',
                  sheet: 'General y Costos',
                });
              }
            });

            // Optional "División": must be one of the exporter divisions.
            const divisionName = String(cleanedRow['División'] || '').trim();
            if (divisionName) {
              const division = divisions.find(
                (d) => d.name.trim().toLowerCase() === divisionName.toLowerCase()
              );
              if (division) {
                cleanedRow['División'] = division.name;
              } else {
                validationErrors.push({
                  row: rowNumber,
                  field: 'División',
                  message: divisions.length
                    ? `División no encontrada. Opciones: ${divisions.map((d) => d.name).join(', ')}`
                    : 'División no encontrada',
                  sheet: 'General y Costos',
                });
              }
            }

            // Validate and convert date fields
            const dateFields: Array<{ field: string }> = [
              { field: 'Fecha de venta' },
              { field: 'ETD' },
              { field: 'ETA' },
            ];

            dateFields.forEach(({ field }) => {
              if (cleanedRow[field]) {
                const parsed = parseDateValue(cleanedRow[field], dateFormat);
                if (parsed) {
                  cleanedRow[field] = parsed;
                } else {
                  validationErrors.push({
                    row: rowNumber,
                    field,
                    message: `Formato de fecha inválido. Valor: "${String(cleanedRow[field])}"`,
                    sheet: 'General y Costos',
                  });
                }
              }
            });

            // Validate numeric fields
            const numericFields = ['Tipo de Cambio'];
            numericFields.forEach((field) => {
              const value = cleanedRow[field as keyof GeneralCostosRow];
              if (value && (isNaN(Number(value)) || Number(value) <= 0)) {
                validationErrors.push({
                  row: rowNumber,
                  field,
                  message: 'Debe ser un número mayor a 0',
                  sheet: 'General y Costos',
                });
              }
            });

            // Validate no negative cost values
            const nonNegativeFields = [
              'Flete (USD)',
              'Impuestos (Moneda Local)',
              'Comisión (Moneda Local)',
              'Repacking (Moneda Local)',
              'Otros Costos (Moneda Local)',
            ];
            nonNegativeFields.forEach((field) => {
              const value = cleanedRow[field as keyof GeneralCostosRow];
              if (value != null && value !== '' && Number(value) < 0) {
                validationErrors.push({
                  row: rowNumber,
                  field,
                  message: 'No puede ser negativo',
                  sheet: 'General y Costos',
                });
              }
            });

            return cleanedRow;
          });

          // Validate and clean Ingresos data
          const processedIngresosData = ingresosData.map((row, index) => {
            const rowNumber = index + 2;
            const cleanedRow = { ...row };

            // Process string fields - trim and uppercase
            const stringFields = [
              'Número de Referencia',
              'Pallet',
              'Variedad',
              'Formato',
              'Calibre',
              'Color',
              'Etiqueta',
              'Categoría',
            ];
            stringFields.forEach((field) => {
              if (cleanedRow[field as keyof IngresosRow]) {
                cleanedRow[field as keyof IngresosRow] = String(
                  cleanedRow[field as keyof IngresosRow]
                )
                  .trim()
                  .toUpperCase();
              }
            });

            // Validate mandatory fields (pallet is now optional)
            const mandatoryFields = [
              'Número de Referencia',
              'Variedad',
              'Formato',
              'Calibre',
              'Etiqueta',
              'Categoría',
              'Peso Bulto',
              'Cantidad de Bultos',
            ];
            if (selectedSpecies?.id === 2) {
              console.log('selectedSpecies', selectedSpecies);
              mandatoryFields.push('Color');
            }
            mandatoryFields.forEach((field) => {
              const value = cleanedRow[field as keyof IngresosRow];
              if (
                !value ||
                (typeof value === 'string' && value.trim() === '')
              ) {
                validationErrors.push({
                  row: rowNumber,
                  field,
                  message: 'Campo obligatorio',
                  sheet: 'Ingresos',
                });
              }
            });

            // Validate Formato
            if (
              cleanedRow.Formato &&
              !validFormatos.includes(cleanedRow.Formato)
            ) {
              validationErrors.push({
                row: rowNumber,
                field: 'Formato',
                message: `Debe ser uno de: ${validFormatos.join(', ')}`,
                sheet: 'Ingresos',
              });
            }

            // Validate Calibre and Color for Cherry (species ID 2)
            if (selectedSpecies?.id == 2) {
              const validCalibers = [
                'L',
                'XL',
                'J',
                '2J',
                '3J',
                '4J',
                '5J',
                'GIA',
                'MIX',
              ];
              const validColors = ['L', 'D', 'DD'];

              if (
                cleanedRow.Calibre &&
                !validCalibers.includes(cleanedRow.Calibre)
              ) {
                validationWarnings.push({
                  row: rowNumber,
                  field: 'Calibre',
                  message: `Calibre ${cleanedRow.Calibre} no estándar para Cerezas (esperado: ${validCalibers.join(', ')}). Se usará como está, pero verifica si es correcto.`,
                  sheet: 'Ingresos',
                });
              }

              if (cleanedRow.Color && !validColors.includes(cleanedRow.Color)) {
                validationErrors.push({
                  row: rowNumber,
                  field: 'Color',
                  message: `Para Cerezas debe ser uno de: ${validColors.join(', ')}`,
                  sheet: 'Ingresos',
                });
              }
            }

            // Validate Peso Bulto > 0
            if (
              cleanedRow['Peso Bulto'] &&
              (isNaN(Number(cleanedRow['Peso Bulto'])) ||
                Number(cleanedRow['Peso Bulto']) <= 0)
            ) {
              validationErrors.push({
                row: rowNumber,
                field: 'Peso Bulto',
                message: 'Debe ser un número mayor a 0',
                sheet: 'Ingresos',
              });
            }

            // Validate Cantidad de Bultos > 0
            if (
              cleanedRow['Cantidad de Bultos'] &&
              (isNaN(Number(cleanedRow['Cantidad de Bultos'])) ||
                Number(cleanedRow['Cantidad de Bultos']) <= 0)
            ) {
              validationErrors.push({
                row: rowNumber,
                field: 'Cantidad de Bultos',
                message: 'Debe ser un número mayor a 0',
                sheet: 'Ingresos',
              });
            }

            // Validate Precio Bulto (Moneda Local) >= 0
            if (
              cleanedRow['Precio Bulto (Moneda Local)'] !== undefined &&
              cleanedRow['Precio Bulto (Moneda Local)'] !== null
            ) {
              if (
                isNaN(Number(cleanedRow['Precio Bulto (Moneda Local)'])) ||
                Number(cleanedRow['Precio Bulto (Moneda Local)']) < 0
              ) {
                validationErrors.push({
                  row: rowNumber,
                  field: 'Precio Bulto (Moneda Local)',
                  message: 'Debe ser un número mayor o igual a 0',
                  sheet: 'Ingresos',
                });
              }
            }

            return cleanedRow;
          });

          // Validate for duplicate reference numbers in General y Costos
          const referenceCount = new Map<string, number[]>();
          processedGeneralData.forEach((row, index) => {
            const reference = String(row['Número de referencia'] || '')
              .trim()
              .toUpperCase();
            if (reference) {
              if (!referenceCount.has(reference)) {
                referenceCount.set(reference, []);
              }
              referenceCount.get(reference)!.push(index + 2); // +2 because Excel starts at row 2
            }
          });

          // Add validation errors for duplicate references
          referenceCount.forEach((rowNumbers, reference) => {
            if (rowNumbers.length > 1) {
              rowNumbers.forEach((rowNumber) => {
                validationErrors.push({
                  row: rowNumber,
                  field: 'Número de referencia',
                  message: `Referencia duplicada "${reference}" encontrada en las filas ${rowNumbers.join(', ')}`,
                  sheet: 'General y Costos',
                });
              });
            }
          });

          // Cross-sheet validation: Validate reference relationships
          const generalReferences = new Set(
            processedGeneralData
              .map((row) =>
                String(row['Número de referencia']).trim().toUpperCase()
              )
              .filter((ref) => ref && ref !== '')
          );

          const ingresosReferences = new Set(
            processedIngresosData
              .map((row) =>
                String(row['Número de Referencia']).trim().toUpperCase()
              )
              .filter((ref) => ref && ref !== '')
          );

          // Check that each settlement has at least one detail
          generalReferences.forEach((ref) => {
            if (!ingresosReferences.has(ref)) {
              // Find the row number in General y Costos for this reference
              const rowIndex = processedGeneralData.findIndex(
                (row) =>
                  String(row['Número de referencia']).trim().toUpperCase() ===
                  ref
              );
              validationErrors.push({
                row: rowIndex + 2,
                field: 'Número de referencia',
                message:
                  'Esta liquidación no tiene detalles de ingresos correspondientes',
                sheet: 'General y Costos',
              });
            }
          });

          // Check that each detail has a corresponding settlement
          processedIngresosData.forEach((row, index) => {
            const ref = String(row['Número de Referencia'])
              .trim()
              .toUpperCase();
            if (ref && !generalReferences.has(ref)) {
              validationErrors.push({
                row: index + 2,
                field: 'Número de Referencia',
                message:
                  'Esta referencia no existe en la hoja "General y Costos"',
                sheet: 'Ingresos',
              });
            }
          });

          // Validate markets and collect invalid ones
          const invalidMarketsMap: Record<number, string> = {};
          const invalidDestinationPortsMap: Record<number, string> = {};
          const invalidShipsMap: Record<number, string> = {};
          const invalidShipTripsMap: Record<number, string> = {};

          processedGeneralData.forEach((row, index) => {
            // Validate Mercado Venta
            const marketValue = String(row['Mercado Venta'] || '').trim();
            if (marketValue) {
              // Try to find market by name or id
              const foundMarket = markets.find(
                (market) =>
                  market.name.toUpperCase() === marketValue.toUpperCase() ||
                  market.id.toString() === marketValue
              );

              if (!foundMarket) {
                invalidMarketsMap[index] = marketValue;
                validationErrors.push({
                  row: index + 2,
                  field: 'Mercado Venta',
                  message: 'Mercado no encontrado en la base de datos',
                  sheet: 'General y Costos',
                });
              } else {
                // Normalize to market name if found by ID
                row['Mercado Venta'] = foundMarket.name;
              }
            }

            // Validate Puerto de Destino
            const destinationPortValue = String(
              row['Puerto de Destino'] || ''
            ).trim();
            if (destinationPortValue) {
              // Try to find market by name or id
              const foundDestinationPort = markets.find(
                (market) =>
                  market.name.toUpperCase() ===
                    destinationPortValue.toUpperCase() ||
                  market.id.toString() === destinationPortValue
              );

              if (!foundDestinationPort) {
                invalidDestinationPortsMap[index] = destinationPortValue;
                validationErrors.push({
                  row: index + 2,
                  field: 'Puerto de Destino',
                  message:
                    'Puerto de destino no encontrado en la base de datos',
                  sheet: 'General y Costos',
                });
              } else {
                // Normalize to market name if found by ID
                row['Puerto de Destino'] = foundDestinationPort.name;
              }
            }
          });

          setInvalidMarkets(invalidMarketsMap);
          setInvalidDestinationPorts(invalidDestinationPortsMap);

          // Validate comparable markets (CNSHA, CNCAN, or empty only)
          const invalidComparableMarketsMap: Record<number, string> = {};
          const validComparableMarkets = ['CNSHA', 'CNCAN'];

          processedGeneralData.forEach((row, index) => {
            const comparableMarketValue = String(
              row['Mercado Comparable'] || ''
            )
              .trim()
              .toUpperCase();
            if (
              comparableMarketValue &&
              !validComparableMarkets.includes(comparableMarketValue)
            ) {
              invalidComparableMarketsMap[index] = comparableMarketValue;
              validationErrors.push({
                row: index + 2,
                field: 'Mercado Comparable',
                message:
                  'Solo se permite CNSHA (Shanghai), CNCAN (Guangzhou) o vacío',
                sheet: 'General y Costos',
              });
            } else if (comparableMarketValue) {
              // Normalize to uppercase
              row['Mercado Comparable'] = comparableMarketValue;
            }
          });

          setInvalidComparableMarkets(invalidComparableMarketsMap);

          // Validate transport fields
          const invalidTransportsMap: Record<number, string> = {};
          const validTransportTypes = ['A', 'M', 'T'];

          processedGeneralData.forEach((row, index) => {
            const transportType = String(row['Tipo Transporte'] || '')
              .trim()
              .toUpperCase();
            const shipName = String(row['Nombre Nave'] || '').trim();
            const etd = String(row['ETD'] || '').trim();
            const eta = String(row['ETA'] || '').trim();

            // Validate transport type
            if (transportType && !validTransportTypes.includes(transportType)) {
              invalidTransportsMap[index] = transportType;
              validationErrors.push({
                row: index + 2,
                field: 'Tipo Transporte',
                message:
                  'Solo se permite A (Aéreo), M (Marítimo) o T (Terrestre)',
                sheet: 'General y Costos',
              });
            } else if (transportType) {
              // Normalize to uppercase
              row['Tipo Transporte'] = transportType;

              // Validate required fields based on transport type
              if (transportType === 'M' && !shipName) {
                validationErrors.push({
                  row: index + 2,
                  field: 'Nombre Nave',
                  message:
                    'Nombre de nave es obligatorio para transporte marítimo (M)',
                  sheet: 'General y Costos',
                });
              }

              if (
                (transportType === 'A' || transportType === 'T') &&
                (!etd || !eta)
              ) {
                if (!etd) {
                  validationErrors.push({
                    row: index + 2,
                    field: 'ETD',
                    message:
                      'ETD es obligatorio para transporte aéreo (A) y terrestre (T)',
                    sheet: 'General y Costos',
                  });
                }
                if (!eta) {
                  validationErrors.push({
                    row: index + 2,
                    field: 'ETA',
                    message:
                      'ETA es obligatorio para transporte aéreo (A) y terrestre (T)',
                    sheet: 'General y Costos',
                  });
                }
              }

              // Try to auto-match ship and trip for Maritime transport
              if (transportType === 'M' && shipName) {
                const destinationPort = String(
                  row['Puerto de Destino'] || ''
                ).trim();
                const eta = row['ETA'];
                const fechaVenta = row['Fecha de venta'];

                // Find ship by name using regex
                const matchedShip = ships.find(
                  (ship) =>
                    new RegExp(ship.name.toLowerCase()).test(
                      shipName.toLowerCase()
                    ) && ship.type === 'M'
                );

                if (matchedShip) {
                  row['_selectedShipId'] = matchedShip.id.toString();
                  row['Nombre Nave'] = matchedShip.name;
                  // Try to find matching trip by destination port + ETA ±7 days
                  if (destinationPort && eta) {
                    // Dates are now already converted to strings during validation
                    const etaDate = new Date(eta);
                    const seasonDate = fechaVenta ? new Date(fechaVenta) : eta;
                    const season = calculateSeasonFromDate(seasonDate);

                    const shipTripsForShip = shipTrips.filter(
                      (trip) =>
                        trip.ship.id === matchedShip.id &&
                        calculateSeasonFromDate(trip.etd) === season
                    );

                    let foundTrip = null;

                    for (const trip of shipTripsForShip) {
                      // Check if destination port matches (by name or ID)
                      const tripMarketName =
                        trip.market?.name?.toUpperCase() || '';
                      const tripMarketId = trip.market?.id?.toString() || '';
                      const destinationUpper = destinationPort.toUpperCase();

                      const destinationMatches =
                        tripMarketName &&
                        tripMarketName !== '' &&
                        (tripMarketName === destinationUpper ||
                          tripMarketId === destinationPort ||
                          tripMarketName.includes(destinationUpper) ||
                          destinationUpper.includes(tripMarketName));

                      // Check ETA within ±7 days
                      const tripEtaDate = new Date(trip.eta);
                      const diffDays = Math.abs(
                        (etaDate.getTime() - tripEtaDate.getTime()) /
                          (1000 * 60 * 60 * 24)
                      );
                      const etaMatches = diffDays <= 7;

                      if (destinationMatches && etaMatches) {
                        // Perfect match: destination + ETA
                        console.log('Initial perfect match found:', trip.id);
                        foundTrip = trip;
                        break;
                      }
                    }

                    // Use best trip if no perfect match found
                    if (!foundTrip) {
                      invalidShipTripsMap[index] = shipName;
                      validationErrors.push({
                        row: index + 2,
                        field: 'Nombre Nave',
                        message: `No se encontró viaje correspondiente para la nave ${shipName} en el mercado ${destinationPort} con ETA ${etaDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}`,
                        sheet: 'General y Costos',
                      });
                    }

                    if (foundTrip) {
                      row['_shipTripId'] = foundTrip.id;
                      row['Puerto de Destino'] = foundTrip.market?.name || '';
                      row['ETA'] = foundTrip.eta;
                    } else {
                      console.log('Initial no suitable trip found');
                    }
                  } else {
                    invalidShipTripsMap[index] = shipName;
                    validationErrors.push({
                      row: index + 2,
                      field: 'Nombre Nave',
                      message: `No se encontró viaje correspondiente para la nave ${shipName} en el mercado ${destinationPort}`,
                      sheet: 'General y Costos',
                    });
                  }
                } else {
                  invalidShipsMap[index] = shipName;
                  validationErrors.push({
                    row: index + 2,
                    field: 'Nombre Nave',
                    message: `Nave ${shipName} no encontrada en la base de datos`,
                    sheet: 'General y Costos',
                  });
                }
              }
            }
          });

          setInvalidTransports(invalidTransportsMap);
          setInvalidShips(invalidShipsMap);
          setInvalidShipTrips(invalidShipTripsMap);

          // Validate settlement existence in database
          if (userProfile?.exporter_id && selectedSpecies?.id && season) {
            setIsValidatingSettlements(true);
            const validationResults: Record<
              string,
              {
          exists: boolean;
          isBlocked?: boolean;
          settlementId?: number;
          divisionId?: number | null;
        }
            > = {};

            try {
              const uniqueReferences = Array.from(generalReferences);
              const referenceDivisions = await resolveReferenceDivisions(
                uniqueReferences,
                processedGeneralData,
                validationErrors
              );
              const existingSettlements =
                await getSettlementNoDetailsByExporterId(
                  userProfile.exporter_id,
                  undefined,
                  season,
                  selectedSpecies.id,
                  'all' // Include all settlements regardless of sinister status
                );

              // A settlement is the same one only within its division.
              for (const reference of uniqueReferences) {
                const divisionId = referenceDivisions[reference] ?? null;
                // Unresolved division (shared reference): the row already
                // carries a validation error, so leave its status unknown.
                if (divisionId === null) continue;
                const existing = existingSettlements?.find(
                  (settlement) =>
                    settlement.reference_number === reference &&
                    (settlement.division_id ?? null) === divisionId
                );

                validationResults[reference] = existing
                  ? {
                      exists: true,
                      isBlocked: existing.is_blocked,
                      settlementId: existing.id,
                      divisionId,
                    }
                  : { exists: false, divisionId };
              }

              setSettlementValidationResults(validationResults);
            } catch (error) {
              console.error('Error validating settlement existence:', error);
              toast.error('Error al validar liquidaciones existentes');
            } finally {
              setIsValidatingSettlements(false);
            }
          }

          const invalidReferencesSet = new Set<string>();

          validationErrors.forEach((error) => {
            if (error.sheet === 'General y Costos') {
              const rowIndex = error.row - 2;
              const ref = String(
                processedGeneralData[rowIndex]['Número de referencia']
              )
                .trim()
                .toUpperCase();
              invalidReferencesSet.add(ref);
            } else if (error.sheet === 'Ingresos') {
              const rowIndex = error.row - 2;
              const ref = String(
                processedIngresosData[rowIndex]['Número de Referencia']
              )
                .trim()
                .toUpperCase();
              invalidReferencesSet.add(ref);
            }
          });

          const invalidGeneralRows = new Set(
            validationErrors
              .filter((e) => e.sheet === 'General y Costos')
              .map((e) => e.row)
          ).size;
          const invalidIngresosRows = new Set(
            validationErrors
              .filter((e) => e.sheet === 'Ingresos')
              .map((e) => e.row)
          ).size;

          const result: ProcessedDataType = {
            generalCostos: processedGeneralData,
            ingresos: processedIngresosData,
            validationErrors,
            validationWarnings,
            invalidReferences: Array.from(invalidReferencesSet),
            summary: {
              totalGeneralRows: processedGeneralData.length,
              totalIngresosRows: processedIngresosData.length,
              validGeneralRows:
                processedGeneralData.length - invalidGeneralRows,
              validIngresosRows:
                processedIngresosData.length - invalidIngresosRows,
              hasErrors: validationErrors.length > 0,
            },
          };

          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsArrayBuffer(file);
    });
  };

  const handleUpload = async () => {
    if (!selectedFile || !season || !selectedSpecies) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setIsUploading(true);
    setUploadStage('settlements');
    try {
      const result = await validateAndProcessExcel(selectedFile);
      setProcessedData(result);

      if (result.summary.hasErrors) {
        toast.warning(
          `Archivo procesado con ${result.validationErrors.length} errores de validación`
        );
      } else {
        toast.success('Archivo procesado exitosamente');
      }

      setStep('review');
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error(
        error instanceof Error ? error.message : 'Error al procesar el archivo'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const resetModal = () => {
    setSeason(globalSeason || '');
    setSelectedSpecies(selectedSpeciesFromStore ?? null);
    setSelectedFile(null);
    setDateFormat('dd-MM-yyyy');
    setDragActive(false);
    setIsUploading(false);
    setProcessedData(null);
    setStep('upload');
    setSettlementValidationResults({});
    setIsValidatingSettlements(false);
    setUploadFailures([]);
    setInvalidMarkets({});
    setEditingMarket(null);
    setInvalidDestinationPorts({});
    setEditingDestinationPort(null);
    setInvalidComparableMarkets({});
    setEditingComparableMarket(null);
    setInvalidTransports({});
    setEditingShip(null);
    setEditingTrip(null);
    setEditingETD(null);
    setEditingETA(null);
    setEditingFechaVenta(null);
    setInvalidShips({});
    setInvalidShipTrips({});
    setReceiverMappingSelections({});
    setReceiverSearchValues({});
    setReceiverAutocompleteOpenKeys({});
    setHiddenReceiverAutocompleteKey(null);
    setCreateReceiverGroup(null);
    setCreateReceiverName('');
    setIsCreatingReceiver(false);
    setUploadProgress({ processed: 0, total: 0 });
    setUploadStage(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const transformProcessedDataToSettlements = (): Array<{
    settlement_data: Record<string, unknown>;
    settlement_details: Record<string, unknown>[];
  }> => {
    if (!processedData || !userProfile?.exporter_id || !selectedSpecies) {
      return [];
    }

    return getValidGeneralRowsForUpload()
      .map((generalRow) => {
        const reference = String(generalRow['Número de referencia'])
          .trim()
          .toUpperCase();
        const legacyReceiver = String(generalRow['Cliente'] || '').trim();
        const receiverResolution = getReceiverResolution(legacyReceiver);
        const relatedIngresos = processedData.ingresos.filter(
          (row) =>
            String(row['Número de Referencia']).trim().toUpperCase() ===
            reference
        );

        // Calculate totals for distribution
        const totalRevenue = relatedIngresos.reduce(
          (sum, row) =>
            sum +
            (Number(row['Precio Bulto (Moneda Local)']) || 0) *
              (Number(row['Cantidad de Bultos']) || 0),
          0
        );
        const totalKilos = relatedIngresos.reduce(
          (sum, row) =>
            sum +
            (Number(row['Peso Bulto']) || 0) *
              (Number(row['Cantidad de Bultos']) || 0),
          0
        );

        // Calculate distribution rates
        const commissionRate =
          totalRevenue > 0
            ? (Number(generalRow['Comisión (Moneda Local)']) || 0) /
              totalRevenue
            : 0;
        const taxRate =
          totalRevenue > 0
            ? (Number(generalRow['Impuestos (Moneda Local)']) || 0) /
              totalRevenue
            : 0;
        const otherCostsPerKilo =
          totalKilos > 0
            ? (Number(generalRow['Otros Costos (Moneda Local)']) || 0) /
              totalKilos
            : 0;
        const repackingCostsPerKilo =
          totalKilos > 0
            ? (Number(generalRow['Repacking (Moneda Local)']) || 0) /
              totalKilos
            : 0;
        const freightPerKilo =
          totalKilos > 0
            ? (Number(generalRow['Flete (USD)']) || 0) / totalKilos
            : 0;

        // Find market by name
        const marketName = String(generalRow['Mercado Venta'] || '').trim();
        const market = markets.find(
          (m) => m.name.toUpperCase() === marketName.toUpperCase()
        );
        if (!market) {
          throw new Error(`Market not found: ${marketName}`);
        }

        // Find comparable market if specified
        let comparableMarketId: string | undefined;
        const comparableMarketValue = String(
          generalRow['Mercado Comparable'] || ''
        )
          .trim()
          .toUpperCase();
        if (comparableMarketValue) {
          // Handle CNSHA and CNCAN codes directly
          if (
            comparableMarketValue === 'CNSHA' ||
            comparableMarketValue === 'CNCAN'
          ) {
            comparableMarketId = comparableMarketValue;
          } else {
            // Try to find by market name (for backward compatibility)
            const comparableMarket = markets.find(
              (m) =>
                m.name.toUpperCase() === comparableMarketValue.toUpperCase()
            );
            if (comparableMarket) {
              comparableMarketId = comparableMarket.id.toString();
            }
          }
        }

        // Handle transport data
        const transportType = String(generalRow['Tipo Transporte'] || '')
          .trim()
          .toUpperCase();
        let shipTripId: number | undefined;
        let shipId: number | undefined;
        let manualTripData: Record<string, unknown> | undefined;

        if (transportType === 'M') {
          // Maritime transport - use ship trip ID
          shipTripId = generalRow['_shipTripId'];
          if (!shipTripId) {
            throw new Error(
              `Ship trip not found for maritime transport in settlement ${reference}`
            );
          }
        } else if (transportType === 'A' || transportType === 'T') {
          // Air or Terrestrial transport - create manual trip data
          const destinationPortName = String(
            generalRow['Puerto de Destino'] || ''
          ).trim();
          const destinationMarket = markets.find(
            (m) => m.name.toUpperCase() === destinationPortName.toUpperCase()
          );
          if (!destinationMarket) {
            throw new Error(
              `Destination market not found: ${destinationPortName}`
            );
          }

          // Find ship by type
          const ship = ships.find((s) => s.type === transportType);
          if (!ship) {
            throw new Error(
              `Ship not found for transport type: ${transportType}`
            );
          }
          shipId = ship.id;

          // Parse dates
          const etdDate = generalRow['ETD'];
          const etaDate = generalRow['ETA'];

          let etdString = '';
          let etaString = '';

          if (etdDate) {
            etdString = new Date(etdDate).toISOString();
          }

          if (etaDate) {
            etaString = new Date(etaDate).toISOString();
          }

          manualTripData = {
            fecha_embarque: etdString,
            destino_market_id: destinationMarket.id.toString(),
            fecha_llegada: etaString,
          };
        }

        // Parse date
        const dateSold = generalRow['Fecha de venta'];
        let dateSoldString = '';
        if (dateSold) {
          dateSoldString = new Date(dateSold).toISOString();
        }

        const settlementData = {
          reference_number: reference,
          receiver: receiverResolution.receiver?.name || legacyReceiver,
          legacy_receiver: legacyReceiver,
          exporter_receiver_id: receiverResolution.receiver?.id || null,
          business_type: String(generalRow['Tipo de Negocio'] || '').trim(),
          sale_order: String(generalRow['Orden de Venta'] || '').trim(),
          container_number: String(
            generalRow['Número de Contenedor'] || ''
          ).trim(),
          date_sold: dateSoldString,
          market_id: market.id,
          comparable_market_id: comparableMarketId,
          region: '',
          total_revenue_local: totalRevenue,
          total_tax_local: Number(generalRow['Impuestos (Moneda Local)']) || 0,
          total_commission_local:
            Number(generalRow['Comisión (Moneda Local)']) || 0,
          total_other_costs_local:
            Number(generalRow['Otros Costos (Moneda Local)']) || 0,
          total_repacking_costs_local:
            Number(generalRow['Repacking (Moneda Local)']) || 0,
          total_freight_usd: Number(generalRow['Flete (USD)']) || 0,
          exchange_rate: Number(generalRow['Tipo de Cambio']) || 1,
          local_currency: String(generalRow['Moneda Local'] || '').trim(),
          original_incoterm: String(
            generalRow['Incoterm Original'] || ''
          ).trim(),
          season: season,
          species_id: selectedSpecies.id,
          division_id: settlementValidationResults[reference]?.divisionId ?? null,
          ...(transportType === 'M'
            ? { ship_trip_id: shipTripId }
            : {
                ship_id: shipId,
                manual_trip_data: manualTripData,
              }),
        };

        // Transform details
        const settlementDetails = relatedIngresos.map((ingresoRow) => {
          const unitPrice =
            Number(ingresoRow['Precio Bulto (Moneda Local)']) || 0;
          const quantity = Number(ingresoRow['Cantidad de Bultos']) || 0;
          const unitWeight = Number(ingresoRow['Peso Bulto']) || 0;
          const totalPrice = unitPrice * quantity;
          const totalKilos = unitWeight * quantity;

          // Distribute costs to each detail
          const detailCommission = totalPrice * commissionRate;
          const detailTax = totalPrice * taxRate;
          const detailOtherCosts = totalKilos * otherCostsPerKilo;
          const detailRepackingCosts = totalKilos * repackingCostsPerKilo;
          const detailFreight = totalKilos * freightPerKilo;
          const detailFob =
            (totalPrice -
              detailTax -
              detailCommission -
              detailOtherCosts -
              detailRepackingCosts) /
              (Number(generalRow['Tipo de Cambio']) || 1) -
            detailFreight;

          return {
            pallet: String(ingresoRow['Pallet'] || '').trim() || '',
            variety: String(ingresoRow['Variedad'] || '').trim(),
            format: String(ingresoRow['Formato'] || '').trim(),
            caliber: String(ingresoRow['Calibre'] || '').trim(),
            color: String(ingresoRow['Color'] || '').trim(),
            category: String(ingresoRow['Categoría'] || '').trim(),
            label: String(ingresoRow['Etiqueta'] || '').trim(),
            unit_weight: unitWeight,
            quantity: quantity,
            kilos: totalKilos,
            local_total_price: totalPrice,
            local_currency: String(generalRow['Moneda Local'] || '').trim(),
            local_total_tax: detailTax,
            local_total_commision: detailCommission,
            local_other_costs: detailOtherCosts,
            local_repacking_costs: detailRepackingCosts,
            exchange_rate: Number(generalRow['Tipo de Cambio']) || 1,
            usd_freight: detailFreight,
            usd_total_fob: detailFob,
            incoterm: String(generalRow['Incoterm Original'] || '').trim(),
            date_sold: dateSoldString,
            quartil_vs_market: null,
            difference_vs_market: null,
            difference_vs_market_eta: null,
            quartil_vs_market_eta: null,
          };
        });

        return {
          settlement_data: settlementData,
          settlement_details: settlementDetails,
        };
      });
  };

  const calculateMarketComparisonWithProgress = async (
    settlementIds: (number | undefined)[]
  ) => {
    if (!session) return;

    try {
      // Process settlements in batches to avoid overwhelming the server
      const batchSize = 5;
      const batches = [];

      for (let i = 0; i < settlementIds.length; i += batchSize) {
        batches.push(settlementIds.slice(i, i + batchSize));
      }

      let processedCount = 0;
      let errorCount = 0;

      for (const batch of batches) {
        // Process each settlement in the batch
        const promises = batch.map(async (settlementId) => {
          if (!settlementId) return;

          try {
            const response = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calculate-settlement-market-comparison`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify({
                  settlement_id: settlementId,
                  // On massive upload, compute both date_sold and ETA metrics
                  modifyMarketDateSold: true,
                  modifyMarketEta: true,
                }),
              }
            );

            if (!response.ok) {
              throw new Error(`Error processing settlement ${settlementId}`);
            }

            processedCount++;
          } catch (error) {
            console.error(
              `Error calculating market comparison for settlement ${settlementId}:`,
              error
            );
            errorCount++;
          } finally {
            setUploadProgress((prev) => ({
              ...prev,
              processed: Math.min(prev.processed + 1, prev.total),
            }));
          }
        });

        // Wait for current batch to complete before processing next batch
        await Promise.all(promises);

        // Small delay between batches to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      return { processedCount, errorCount };
    } catch (error) {
      console.error('Error in market comparison calculation:', error);
      return { processedCount: 0, errorCount: settlementIds.length };
    }
  };

  const handleMassiveUpload = async () => {
    if (
      !processedData ||
      !userProfile?.exporter_id ||
      !selectedSpecies ||
      !session
    ) {
      toast.error('Error: Datos incompletos para la subida');
      return;
    }

    if (isLoadingReceivers) {
      toast.info('Aún se están cargando los clientes oficiales');
      return;
    }

    if (unresolvedReceiverGroups.length > 0) {
      toast.error('Asocia todos los clientes a un cliente oficial antes de subir');
      return;
    }

    setIsUploading(true);
    setUploadFailures([]);
    try {
      // Transform processed data to settlement format
      const settlements = transformProcessedDataToSettlements();

      if (settlements.length === 0) {
        toast.error('No hay liquidaciones válidas para subir');
        return;
      }

      const batchSize = 20;
      const batches = [];

      for (let i = 0; i < settlements.length; i += batchSize) {
        batches.push(settlements.slice(i, i + batchSize));
      }

      setUploadProgress({ processed: 0, total: settlements.length });

      let aggregatedResults: Record<string, unknown>[] = [];
      let summary = { successful: 0, failed: 0 };

      for (const batch of batches) {
        // Call the massive upload API in batches of 20 references
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-settlements-massive`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              settlements: batch,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || 'Error al subir las liquidaciones'
          );
        }

        const result = await response.json();
        const batchResults = Array.isArray(result.results)
          ? result.results
          : [];
        const batchSummary = result.summary || { successful: 0, failed: 0 };

        aggregatedResults = aggregatedResults.concat(batchResults);
        summary = {
          successful:
            summary.successful + (Number(batchSummary.successful) || 0),
          failed: summary.failed + (Number(batchSummary.failed) || 0),
        };

        setUploadProgress((prev) => ({
          ...prev,
          processed: Math.min(prev.processed + batch.length, prev.total),
        }));
      }

      const result = {
        summary,
        results: aggregatedResults,
      };

      // Show results
      const successfulCount = result.summary.successful;
      const failedCount = result.summary.failed;

      if (failedCount === 0) {
        toast.success(
          `¡Éxito! Se subieron ${successfulCount} liquidaciones correctamente`
        );
      } else {
        toast.warning(
          `Procesamiento completado: ${successfulCount} exitosas, ${failedCount} fallidas`
        );
      }

      // Show detailed results if there are failures
      if (failedCount > 0) {
        const failedSettlements = result.results.filter(
          (r: Record<string, unknown>) => !r.success
        );
        setUploadFailures(
          failedSettlements.map((r: Record<string, unknown>) => ({
            reference: String(r.reference_number ?? ''),
            error: String(r.error ?? 'Error desconocido'),
          }))
        );
      }

      // Start market comparison calculation for successful settlements
      if (successfulCount > 0) {
        const successfulSettlements = result.results.filter(
          (r: Record<string, unknown>) => r.success
        );
        const settlementIds = successfulSettlements
          .map((r: Record<string, unknown>) => r.settlement_id)
          .filter(Boolean);

        if (settlementIds.length > 0) {
          setUploadStage('comparison');
          setUploadProgress({ processed: 0, total: settlementIds.length });
          const comparisonResult =
            await calculateMarketComparisonWithProgress(settlementIds);

          if (comparisonResult?.errorCount === 0) {
            toast.success(
              `✅ Comparación de precios calculada exitosamente para ${comparisonResult?.processedCount ?? 0} liquidaciones`
            );
          } else {
            toast.warning(
              `⚠️ Comparación completada: ${comparisonResult?.processedCount ?? 0} exitosas, ${comparisonResult?.errorCount ?? 0} fallidas`
            );
          }
        }
      }

      if (failedCount > 0) {
        // Keep the review open so the failures can be read and fixed.
        onSuccess();
      } else {
        resetModal();
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error uploading settlements:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Error al subir las liquidaciones'
      );
    } finally {
      setIsUploading(false);
      setUploadStage(null);
      setUploadProgress({ processed: 0, total: 0 });
    }
  };

  const currentInvalidReferences = processedData
    ? (() => {
        const invalidRefs = new Set<string>();

        // Add references with General y Costos validation errors
        processedData.validationErrors
          ?.filter((error) => error.sheet === 'General y Costos')
          ?.forEach((error) => {
            const rowIndex = error.row - 2;
            const ref = String(
              processedData.generalCostos[rowIndex]?.['Número de referencia'] ||
                ''
            )
              .trim()
              .toUpperCase();
            if (ref) invalidRefs.add(ref);
          });

        // Add references with Ingresos validation errors
        processedData.validationErrors
          ?.filter((error) => error.sheet === 'Ingresos')
          ?.forEach((error) => {
            const rowIndex = error.row - 2;
            const ingresosRow = processedData.ingresos[rowIndex];
            if (ingresosRow) {
              const ref = String(ingresosRow['Número de Referencia'] || '')
                .trim()
                .toUpperCase();
              if (ref) invalidRefs.add(ref);
            }
          });

        return Array.from(invalidRefs);
      })()
    : [];

  const handleBackToUpload = () => {
    resetModal();
  };

  // Helper functions for ship and trip selection
  const getMaritimeShips = () => {
    return ships.filter((ship) => ship.type === 'M');
  };

  const getTripsByShip = (shipId: string) => {
    if (!shipId || !season) return [];
    return shipTrips.filter(
      (trip) =>
        trip.ship.id.toString() === shipId &&
        calculateSeasonFromDate(trip.eta) === season
    );
  };

  const handleShipSelection = (rowIndex: number, shipId: string) => {
    if (!processedData) return;

    const selectedShip = ships.find((s) => s.id.toString() === shipId);

    if (selectedShip) {
      updateRowAndClearErrors(
        rowIndex,
        {
          'Nombre Nave': selectedShip.name,
          _selectedShipId: shipId,
          _shipTripId: null, // Clear trip when ship changes
          'Puerto de Destino': '',
          ETA: '',
        },
        ['Nombre Nave', 'Viaje de Nave']
      ); // Clear ship and trip errors
    }
    setEditingShip(null);
  };

  const handleTripSelection = (rowIndex: number, tripId: string) => {
    if (!processedData) return;

    const selectedTrip = shipTrips.find((t) => t.id.toString() === tripId);

    if (selectedTrip) {
      updateRowAndClearErrors(
        rowIndex,
        {
          _shipTripId: selectedTrip.id,
          'Puerto de Destino': selectedTrip.market?.name || '',
          ETA: selectedTrip.eta,
        },
        ['Nombre Nave', 'Viaje de Nave']
      ); // Clear ship and trip errors
    }
    setEditingTrip(null);
  };

  const calculateSettlementTotals = (
    generalRow: GeneralCostosRow,
    ingresosData: IngresosRow[]
  ) => {
    const reference = String(generalRow['Número de referencia'])
      .trim()
      .toUpperCase();
    const relatedIngresos = ingresosData.filter(
      (row) =>
        String(row['Número de Referencia']).trim().toUpperCase() === reference
    );

    const totalIngresos = relatedIngresos.reduce((sum, row) => {
      const precio = Number(row['Precio Bulto (Moneda Local)']) || 0;
      const cantidad = Number(row['Cantidad de Bultos']) || 0;
      return sum + precio * cantidad;
    }, 0);

    const totalBultos = relatedIngresos.reduce((sum, row) => {
      return sum + (Number(row['Cantidad de Bultos']) || 0);
    }, 0);

    const totalKilos = relatedIngresos.reduce((sum, row) => {
      const peso = Number(row['Peso Bulto']) || 0;
      const cantidad = Number(row['Cantidad de Bultos']) || 0;
      return sum + peso * cantidad;
    }, 0);

    return {
      totalIngresos,
      totalBultos,
      totalKilos,
    };
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  // Add this function after the existing helper functions
  const updateRowAndClearErrors = (
    rowIndex: number,
    updates: Record<string, string | number | null>,
    errorFieldsToClear: string[]
  ) => {
    if (!processedData) return;

    const updatedData = { ...processedData };

    // Update the specific row data
    updatedData.generalCostos[rowIndex] = {
      ...updatedData.generalCostos[rowIndex],
      ...updates,
    };

    // Clear specific validation errors for this row
    updatedData.validationErrors = updatedData.validationErrors.filter(
      (error) =>
        !(
          error.sheet === 'General y Costos' &&
          error.row === rowIndex + 2 &&
          errorFieldsToClear.includes(error.field)
        )
    );

    // Clear invalid market/destination port indicators when valid values are selected
    if (
      errorFieldsToClear.includes('Mercado Venta') &&
      updates['Mercado Venta']
    ) {
      // Check if the selected market is valid
      const selectedMarket = String(updates['Mercado Venta']).trim();
      const isValidMarket = markets.some(
        (market) => market.name.toUpperCase() === selectedMarket.toUpperCase()
      );

      if (isValidMarket) {
        setInvalidMarkets((prev) => {
          const newState = { ...prev };
          delete newState[rowIndex];
          return newState;
        });
      }
    }

    if (
      errorFieldsToClear.includes('Puerto de Destino') &&
      updates['Puerto de Destino']
    ) {
      // Check if the selected destination port is valid
      const selectedPort = String(updates['Puerto de Destino']).trim();
      const isValidPort = markets.some(
        (market) => market.name.toUpperCase() === selectedPort.toUpperCase()
      );

      if (isValidPort) {
        setInvalidDestinationPorts((prev) => {
          const newState = { ...prev };
          delete newState[rowIndex];
          return newState;
        });
      }
    }

    // Update summary counts
    const invalidGeneralRows = new Set(
      updatedData.validationErrors
        .filter((e) => e.sheet === 'General y Costos')
        .map((e) => e.row)
    ).size;
    updatedData.summary = {
      ...updatedData.summary,
      validGeneralRows: updatedData.generalCostos.length - invalidGeneralRows,
      hasErrors: updatedData.validationErrors.length > 0,
    };

    setProcessedData(updatedData);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size='full'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            <h3 className='text-xl font-bold'>
              Subir Liquidaciones vía Excel Masivo
            </h3>
            <p className='text-sm text-default-500'>
              Descarga la plantilla, complétala con tus datos y súbela para
              procesar múltiples liquidaciones
            </p>
          </ModalHeader>
          <ModalBody className='max-h-[80vh] overflow-y-auto'>
            {step === 'upload' ? (
              <div className='space-y-6'>
                {/* Step 1: Select Species and Season */}
                <div className='space-y-4'>
                  <h4 className='text-lg font-semibold text-foreground'>
                    Paso 1: Selecciona Especie y Temporada
                  </h4>

                  <div className='grid grid-cols-3 gap-4'>
                    <Select
                      label='Temporada *'
                      selectedKeys={[season]}
                      onChange={(e) => setSeason(e.target.value)}
                      className='w-full'
                      size='sm'
                    >
                      {Object.values(SEASONS).map((s) => (
                        <SelectItem key={s.name} value={s.name}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </Select>

                    <Select
                      label='Especie *'
                      selectedKeys={
                        selectedSpecies ? [selectedSpecies.id.toString()] : []
                      }
                      onChange={(e) => {
                        const sp = species.find(
                          (sp) => sp.id.toString() === e.target.value
                        );
                        setSelectedSpecies(sp || null);
                      }}
                      className='w-full'
                      size='sm'
                    >
                      {species.map((sp) => (
                        <SelectItem key={sp.id} value={sp.id.toString()}>
                          {sp.name}
                        </SelectItem>
                      ))}
                    </Select>

                    <Select
                      label='Formato de fechas (texto)'
                      selectedKeys={[dateFormat]}
                      onChange={(e) => setDateFormat(e.target.value)}
                      className='w-full'
                      size='sm'
                      description='Solo aplica si las fechas son texto, no número Excel'
                    >
                      {DATE_FORMAT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>

                <Divider />

                {/* Step 2: Download Template */}
                <div className='space-y-4'>
                  <h4 className='text-lg font-semibold text-foreground'>
                    Paso 2: Descarga la Plantilla
                  </h4>

                  <div className='space-y-4'>
                    <div className='bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
                      <div className='flex items-start space-x-3'>
                        <Icon icon='tabler:file-spreadsheet' className='text-blue-600 dark:text-blue-400 text-xl mt-1' />
                        <div className='flex-1'>
                          <h5 className='font-medium text-blue-900 dark:text-blue-200'>
                            Plantilla Excel con dos hojas:
                          </h5>
                          <ul className='text-sm text-blue-800 dark:text-blue-300 mt-2 space-y-1'>
                            <li>
                              • <strong>General y Costos:</strong> Datos
                              generales de la liquidación. Una línea por
                              liquidación.
                              {divisions.length > 1 &&
                                ' La columna "División" indica a qué división pertenece; es obligatoria cuando la referencia existe en más de una.'}
                            </li>
                            <li>
                              • <strong>Ingresos:</strong> Detalle por pallet de
                              productos. Una línea por pallet o detalle de
                              venta. Incluye Número de Referencia para vincular
                              con la liquidación.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className='flex gap-3'>
                      <Button
                        variant='flat'
                        onClick={() => setIsInstructionsModalOpen(true)}
                        className='flex-shrink-0'
                      >
                        Ver instrucciones
                      </Button>
                      <Button
                        color='primary'
                        onClick={generateExcelTemplate}
                        disabled={!season || !selectedSpecies}
                        startContent={<Icon icon='tabler:download' />}
                        className='flex-grow'
                      >
                        Descargar Plantilla Excel
                      </Button>
                    </div>
                  </div>
                </div>

                <Divider />

                {/* Step 3: Upload Completed File */}
                <div className='space-y-4'>
                  <h4 className='text-lg font-semibold text-foreground'>
                    Paso 3: Sube el Archivo
                  </h4>

                  {/* File upload area */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                      dragActive
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-divider hover:border-default-400'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {selectedFile ? (
                      <div className='space-y-3'>
                        <div className='flex items-center justify-center space-x-2'>
                          <Icon icon='tabler:file-spreadsheet' className='text-green-600 dark:text-green-400 text-2xl' />
                          <div className='text-left'>
                            <p className='font-medium text-foreground'>
                              {selectedFile.name}
                            </p>
                            <p className='text-sm text-default-400'>
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>

                        <div className='flex justify-center space-x-2'>
                          <Button
                            size='sm'
                            variant='light'
                            onClick={removeFile}
                          >
                            Quitar archivo
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className='space-y-3'>
                        <Icon icon='tabler:upload' width={36} className='mx-auto text-default-300' />
                        <div>
                          <p className='text-lg font-medium text-foreground'>
                            Arrastra tu archivo Excel aquí
                          </p>
                          <p className='text-sm text-default-400'>
                            o haz clic para seleccionar
                          </p>
                        </div>

                        <input
                          type='file'
                          accept='.xlsx,.xls'
                          onChange={handleFileInput}
                          className='hidden'
                          id='file-upload'
                        />

                        <Button
                          variant='flat'
                          onClick={() =>
                            document.getElementById('file-upload')?.click()
                          }
                        >
                          Seleccionar archivo
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className='space-y-6'>
                {/* Review Step */}
                <div className='space-y-4'>
                  <div className='flex items-center justify-between'></div>
                  {/* Summary Cards */}
                  <div className='grid grid-cols-3 gap-4'>
                    <div className='col-span-1'>
                      <div className='grid grid-cols-2 gap-4'>
                        <div className='bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
                          <h5 className='font-medium text-blue-900 dark:text-blue-200'>
                            Hoja General y Costos
                          </h5>
                          <p className='text-sm text-blue-800 dark:text-blue-300'>
                            {processedData?.summary?.validGeneralRows} de{' '}
                            {processedData?.summary?.totalGeneralRows}{' '}
                            referencias válidas
                          </p>
                        </div>
                        <div className='bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
                          <h5 className='font-medium text-blue-900 dark:text-blue-200'>
                            Hoja Ingresos
                          </h5>
                          <p className='text-sm text-blue-800 dark:text-blue-300'>
                            {processedData?.summary?.validIngresosRows} de{' '}
                            {processedData?.summary?.totalIngresosRows} filas
                            válidas
                          </p>
                        </div>
                      </div>
                      {uploadFailures.length > 0 && (
                        <div className='bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4'>
                          <h5 className='font-medium text-red-900 dark:text-red-200 mb-3'>
                            Liquidaciones no subidas ({uploadFailures.length})
                          </h5>
                          <ul className='max-h-60 overflow-y-auto space-y-1 text-sm text-red-800 dark:text-red-300'>
                            {uploadFailures.map((failure, index) => (
                              <li key={`${failure.reference}-${index}`}>
                                <span className='font-mono font-medium'>
                                  {failure.reference}
                                </span>
                                : {failure.error}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {/* Validation Errors */}
                      {processedData &&
                        processedData.validationErrors &&
                        (() => {
                          const errors = processedData.validationErrors;
                          const generalData = processedData.generalCostos;
                          const ingresosData = processedData.ingresos;
                          return (
                            <div className='bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4'>
                              <h5 className='font-medium text-red-900 dark:text-red-200 mb-3'>
                                Errores de Validación ({errors.length})
                              </h5>
                              <Tabs
                                aria-label='Errores por hoja'
                                variant='underlined'
                              >
                                <Tab
                                  key='general'
                                  title={`General y Costos (${errors.filter((error) => error.sheet === 'General y Costos').length})`}
                                >
                                  <div className='max-h-60 overflow-y-auto space-y-4'>
                                    {errors.filter(
                                      (error) =>
                                        error.sheet === 'General y Costos'
                                    ).length > 0 ? (
                                      (() => {
                                        const groupedErrors = new Map<
                                          string,
                                          ValidationError[]
                                        >();
                                        errors
                                          .filter(
                                            (error) =>
                                              error.sheet === 'General y Costos'
                                          )
                                          .forEach((error) => {
                                            const ref =
                                              generalData[error.row - 2]?.[
                                                'Número de referencia'
                                              ] ?? 'N/A';
                                            if (!groupedErrors.has(ref)) {
                                              groupedErrors.set(ref, []);
                                            }
                                            groupedErrors.get(ref)!.push(error);
                                          });

                                        const sortedRefs = Array.from(
                                          groupedErrors.keys()
                                        ).sort();

                                        return sortedRefs.map((ref) => (
                                          <div key={ref} className='space-y-1'>
                                            <div className='font-medium text-red-900 dark:text-red-200 text-sm'>
                                              Ref {ref}
                                            </div>
                                            {groupedErrors
                                              .get(ref)!
                                              .map((error, idx) => (
                                                <div
                                                  key={idx}
                                                  className='text-sm text-red-800 dark:text-red-300 ml-2'
                                                >
                                                  - Campo "{error.field}":{' '}
                                                  {error.message} (Fila{' '}
                                                  {error.row})
                                                </div>
                                              ))}
                                          </div>
                                        ));
                                      })()
                                    ) : (
                                      <div className='text-sm text-default-500'>
                                        No hay errores en esta hoja
                                      </div>
                                    )}
                                  </div>
                                </Tab>
                                <Tab
                                  key='ingresos'
                                  title={`Ingresos (${errors.filter((error) => error.sheet === 'Ingresos').length})`}
                                >
                                  <div className='max-h-60 overflow-y-auto space-y-2'>
                                    {errors.filter(
                                      (error) => error.sheet === 'Ingresos'
                                    ).length > 0 ? (
                                      errors
                                        .filter(
                                          (error) => error.sheet === 'Ingresos'
                                        )
                                        .map((error, index) => (
                                          <div key={index} className='text-sm'>
                                            <div className='font-medium text-red-900 dark:text-red-200'>
                                              Fila {error.row} (Referencia:{' '}
                                              {error.sheet ===
                                              'General y Costos'
                                                ? (generalData[error.row - 2]?.[
                                                    'Número de referencia'
                                                  ] ?? 'N/A')
                                                : (ingresosData[
                                                    error.row - 2
                                                  ]?.['Número de Referencia'] ??
                                                  'N/A')}
                                              )
                                            </div>
                                            <div className='text-red-800 dark:text-red-300 ml-2'>
                                              Campo "{error.field}":{' '}
                                              {error.message}
                                            </div>
                                          </div>
                                        ))
                                    ) : (
                                      <div className='text-sm text-default-500'>
                                        No hay errores en esta hoja
                                      </div>
                                    )}
                                  </div>
                                </Tab>
                              </Tabs>

                              {/* Show summary of reference issues if any */}
                              {errors.some(
                                (e) =>
                                  e.message.includes('detalles de ingresos') ||
                                  e.message.includes('no existe en la hoja') ||
                                  e.message.includes('Referencia duplicada')
                              ) && (
                                <div className='mt-3 p-3 bg-red-100 dark:bg-red-900/30 rounded border border-red-300 dark:border-red-700'>
                                  <p className='text-sm text-red-900 dark:text-red-200 font-medium'>
                                    💡 Problemas de Referencias Detectados:
                                  </p>
                                  <p className='text-xs text-red-800 dark:text-red-300 mt-1'>
                                    • Verifica que cada liquidación en "General
                                    y Costos" tenga al menos una línea en
                                    "Ingresos"
                                    <br />
                                    • Todas las referencias en "Ingresos" deben
                                    existir en "General y Costos"
                                    <br />• No debe haber referencias duplicadas
                                    en "General y Costos"
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                      {processedData &&
                        processedData.validationWarnings &&
                        (() => {
                          const warnings = processedData.validationWarnings;
                          const generalData = processedData.generalCostos;
                          const ingresosData = processedData.ingresos;
                          return (
                            <div className='bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mt-4'>
                              <h5 className='font-medium text-yellow-900 dark:text-yellow-200 mb-3'>
                                Advertencias ({warnings.length})
                              </h5>
                              <Tabs
                                aria-label='Advertencias por hoja'
                                variant='underlined'
                              >
                                <Tab
                                  key='general'
                                  title={`General y Costos (${warnings.filter((w) => w.sheet === 'General y Costos').length})`}
                                >
                                  <div className='max-h-60 overflow-y-auto space-y-2'>
                                    {warnings.filter(
                                      (w) => w.sheet === 'General y Costos'
                                    ).length > 0 ? (
                                      warnings
                                        .filter(
                                          (w) => w.sheet === 'General y Costos'
                                        )
                                        .map((w, index) => (
                                          <div key={index} className='text-sm'>
                                            <div className='font-medium text-yellow-900 dark:text-yellow-200'>
                                              Fila {w.row} (Referencia:{' '}
                                              {w.sheet === 'General y Costos'
                                                ? (generalData[w.row - 2]?.[
                                                    'Número de referencia'
                                                  ] ?? 'N/A')
                                                : (ingresosData[w.row - 2]?.[
                                                    'Número de Referencia'
                                                  ] ?? 'N/A')}
                                              )
                                            </div>
                                            <div className='text-yellow-800 dark:text-yellow-300 ml-2'>
                                              Campo "{w.field}": {w.message}
                                            </div>
                                          </div>
                                        ))
                                    ) : (
                                      <div className='text-sm text-default-500'>
                                        No hay advertencias en esta hoja
                                      </div>
                                    )}
                                  </div>
                                </Tab>
                                <Tab
                                  key='ingresos'
                                  title={`Ingresos (${warnings.filter((w) => w.sheet === 'Ingresos').length})`}
                                >
                                  <div className='max-h-60 overflow-y-auto space-y-2'>
                                    {warnings.filter(
                                      (w) => w.sheet === 'Ingresos'
                                    ).length > 0 ? (
                                      warnings
                                        .filter((w) => w.sheet === 'Ingresos')
                                        .map((w, index) => (
                                          <div key={index} className='text-sm'>
                                            <div className='font-medium text-yellow-900 dark:text-yellow-200'>
                                              Fila {w.row} (Referencia:{' '}
                                              {w.sheet === 'General y Costos'
                                                ? (generalData[w.row - 2]?.[
                                                    'Número de referencia'
                                                  ] ?? 'N/A')
                                                : (ingresosData[w.row - 2]?.[
                                                    'Número de Referencia'
                                                  ] ?? 'N/A')}
                                              )
                                            </div>
                                            <div className='text-yellow-800 dark:text-yellow-300 ml-2'>
                                              Campo "{w.field}": {w.message}
                                            </div>
                                          </div>
                                        ))
                                    ) : (
                                      <div className='text-sm text-default-500'>
                                        No hay advertencias en esta hoja
                                      </div>
                                    )}
                                  </div>
                                </Tab>
                              </Tabs>
                            </div>
                          );
                        })()}
                    </div>
                    {processedData && (
                      <div className='col-span-2'>
                        {/* Summary Totals */}
                        <div className='grid grid-cols-3 gap-4'>
                          <div className='bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center'>
                            <p className='text-sm text-green-800 dark:text-green-200 font-medium'>
                              Total Ingresos (USD)
                            </p>
                            <p className='text-lg font-bold text-green-700 dark:text-green-300'>
                              $
                              {processedData?.generalCostos
                                ?.reduce((total, row) => {
                                  const settlementTotals =
                                    calculateSettlementTotals(
                                      row,
                                      processedData.ingresos
                                    );
                                  const exchangeRate =
                                    Number(row['Tipo de Cambio']) || 1;
                                  return (
                                    total +
                                    settlementTotals.totalIngresos /
                                      exchangeRate
                                  );
                                }, 0)
                                .toLocaleString('es-ES', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                            </p>
                          </div>
                          <div className='bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center'>
                            <p className='text-sm text-green-800 dark:text-green-200 font-medium'>
                              Total Bultos
                            </p>
                            <p className='text-lg font-bold text-green-700 dark:text-green-300'>
                              {processedData.generalCostos
                                .reduce((sum, row) => {
                                  const totals = calculateSettlementTotals(
                                    row,
                                    processedData.ingresos
                                  );
                                  return sum + totals.totalBultos;
                                }, 0)
                                .toLocaleString('es-ES')}
                            </p>
                          </div>
                          <div className='bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center'>
                            <p className='text-sm text-green-800 dark:text-green-200 font-medium'>
                              Total Kilos
                            </p>
                            <p className='text-lg font-bold text-green-700 dark:text-green-300'>
                              {processedData.generalCostos
                                .reduce((sum, row) => {
                                  const totals = calculateSettlementTotals(
                                    row,
                                    processedData.ingresos
                                  );
                                  return sum + totals.totalKilos;
                                }, 0)
                                .toLocaleString('es-ES', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{' '}
                              kg
                            </p>
                          </div>
                        </div>

                        <div
                          className={`mt-4 rounded-lg border p-4 ${
                            unresolvedReceiverGroups.length > 0
                              ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950'
                              : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
                          }`}
                        >
                          <div className='flex items-start justify-between gap-3'>
                            <div>
                              <h5
                                className={`font-medium ${
                                  unresolvedReceiverGroups.length > 0
                                    ? 'text-amber-900 dark:text-amber-200'
                                    : 'text-green-900 dark:text-green-200'
                                }`}
                              >
                                Clientes oficiales
                              </h5>
                              <p
                                className={`text-sm ${
                                  unresolvedReceiverGroups.length > 0
                                    ? 'text-amber-800 dark:text-amber-300'
                                    : 'text-green-800 dark:text-green-300'
                                }`}
                              >
                                {isLoadingReceivers
                                  ? 'Cargando lista de clientes oficiales...'
                                  : unresolvedReceiverGroups.length > 0
                                    ? `${unresolvedReceiverCount} liquidaciones tienen cliente sin asociación oficial.`
                                    : 'Todas las liquidaciones válidas tienen cliente oficial asociado.'}
                              </p>
                              <p className='mt-1 text-xs text-default-500'>
                                Esta lista proviene de tu Configuración,
                                sección Clientes.
                              </p>
                            </div>
                            {!isLoadingReceivers &&
                              unresolvedReceiverGroups.length > 0 && (
                                <div className='text-sm font-medium text-amber-900 dark:text-amber-200'>
                                  {unresolvedReceiverGroups.length}{' '}
                                  {unresolvedReceiverGroups.length === 1
                                    ? 'nombre'
                                    : 'nombres'}
                                </div>
                              )}
                          </div>

                          {!isLoadingReceivers &&
                            unresolvedReceiverGroups.length > 0 && (
                              <div className='mt-4 grid grid-cols-1 gap-3'>
                                {unresolvedReceiverGroups.map((group) => (
                                  <div
                                    key={group.key}
                                    className='grid grid-cols-1 gap-3 rounded-md border border-amber-200 bg-content1 p-3 dark:border-amber-800 md:grid-cols-[1fr_320px]'
                                  >
                                    <div>
                                      <p className='font-medium text-foreground'>
                                        {group.receiverName}
                                      </p>
                                      <p className='text-sm text-default-500'>
                                        {group.count}{' '}
                                        {group.count === 1
                                          ? 'liquidación'
                                          : 'liquidaciones'}
                                        {group.references.length > 0
                                          ? `: ${group.references
                                              .slice(0, 4)
                                              .join(', ')}${
                                              group.references.length > 4
                                                ? '...'
                                                : ''
                                            }`
                                          : ''}
                                      </p>
                                    </div>
                                    <div className='space-y-2'>
                                      {hiddenReceiverAutocompleteKey ===
                                      group.key ? (
                                        <Input
                                          label='Cliente oficial'
                                          value='Creando nuevo cliente...'
                                          isDisabled
                                        />
                                      ) : (
                                        <Autocomplete
                                          label='Cliente oficial'
                                          selectedKey={
                                            receiverMappingSelections[
                                              group.key
                                            ] || null
                                          }
                                          isOpen={
                                            closingReceiverAutocompleteKeyRef.current ===
                                              group.key ||
                                            createReceiverGroup?.key ===
                                              group.key
                                              ? false
                                              : receiverAutocompleteOpenKeys[
                                                  group.key
                                                ] || false
                                          }
                                          onOpenChange={(open) => {
                                            if (
                                              closingReceiverAutocompleteKeyRef.current ===
                                                group.key ||
                                              createReceiverGroup?.key ===
                                                group.key
                                            ) {
                                              return;
                                            }

                                            setReceiverAutocompleteOpenKeys(
                                              (prev) => ({
                                                ...prev,
                                                [group.key]: open,
                                              })
                                            );
                                          }}
                                          onInputChange={(value) =>
                                            setReceiverSearchValues((prev) => ({
                                              ...prev,
                                              [group.key]: value,
                                            }))
                                          }
                                          onSelectionChange={(key) => {
                                            const selectedKey =
                                              key?.toString() || '';
                                            if (
                                              selectedKey ===
                                              CREATE_RECEIVER_OPTION_KEY
                                            ) {
                                              openCreateReceiverModal(group);
                                              return;
                                            }

                                            setReceiverMappingSelections(
                                              (prev) => ({
                                                ...prev,
                                                [group.key]: selectedKey,
                                              })
                                            );
                                            setReceiverAutocompleteOpenKeys(
                                              (prev) => ({
                                                ...prev,
                                                [group.key]: false,
                                              })
                                            );
                                          }}
                                          isRequired
                                        >
                                          <AutocompleteItem
                                            key={CREATE_RECEIVER_OPTION_KEY}
                                            textValue={`Crear Nuevo Cliente ${
                                              receiverSearchValues[group.key] ||
                                              group.receiverName
                                            }`}
                                          >
                                            + Crear Nuevo Cliente
                                          </AutocompleteItem>
                                          {exporterReceivers.map((receiver) => (
                                            <AutocompleteItem
                                              key={receiver.id.toString()}
                                              textValue={receiver.name}
                                            >
                                              {receiver.name}
                                            </AutocompleteItem>
                                          ))}
                                        </Autocomplete>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>

                        {/* Settlements Summary Table */}
                        <div>
                          <div className='flex items-center justify-between my-4'>
                            <div className='flex gap-2'>
                              <div className='text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950 px-2 py-1 rounded border border-green-200 dark:border-green-800'>
                                {`${
                                  Object.values(
                                    settlementValidationResults
                                  ).filter(
                                    (result: {
                                      exists: boolean;
                                      isBlocked?: boolean;
                                      settlementId?: number;
                                    }) => !result.exists
                                  ).length
                                } nuevas`}
                              </div>
                              <div className='text-sm text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950 px-2 py-1 rounded border border-yellow-200 dark:border-yellow-800'>
                                {`${
                                  Object.values(
                                    settlementValidationResults
                                  ).filter(
                                    (result: {
                                      exists: boolean;
                                      isBlocked?: boolean;
                                      settlementId?: number;
                                    }) => result.exists && !result.isBlocked
                                  ).length
                                } sobrescritas`}
                              </div>
                              <div className='text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 px-2 py-1 rounded border border-red-200 dark:border-red-800'>
                                {`${
                                  Object.values(
                                    settlementValidationResults
                                  ).filter(
                                    (result: {
                                      exists: boolean;
                                      isBlocked?: boolean;
                                      settlementId?: number;
                                    }) => result.exists && result.isBlocked
                                  ).length
                                } bloqueadas`}
                              </div>
                            </div>
                            <div className='flex gap-2'>
                              {Object.keys(invalidMarkets).length > 0 && (
                                <div className='text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950 px-2 py-1 rounded border border-amber-200 dark:border-amber-800'>
                                  ⚠️ {Object.keys(invalidMarkets).length}{' '}
                                  mercado(s) de venta no válido(s)
                                </div>
                              )}
                              {Object.keys(invalidDestinationPorts).length >
                                0 && (
                                <div className='text-sm text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950 px-2 py-1 rounded border border-purple-200 dark:border-purple-800'>
                                  ⚠️{' '}
                                  {Object.keys(invalidDestinationPorts).length}{' '}
                                  puerto(s) de destino no válido(s)
                                </div>
                              )}
                              {Object.keys(invalidComparableMarkets).length >
                                0 && (
                                <div className='text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 px-2 py-1 rounded border border-red-200 dark:border-red-800'>
                                  ⚠️{' '}
                                  {Object.keys(invalidComparableMarkets).length}{' '}
                                  mercado(s) comparable(s) no válido(s)
                                </div>
                              )}
                              {Object.keys(invalidTransports).length > 0 && (
                                <div className='text-sm text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-950 px-2 py-1 rounded border border-orange-200 dark:border-orange-800'>
                                  ⚠️ {Object.keys(invalidTransports).length}{' '}
                                  transporte(s) no válido(s)
                                </div>
                              )}
                              {Object.keys(invalidShips).length > 0 && (
                                <div className='text-sm text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-950 px-2 py-1 rounded border border-orange-200 dark:border-orange-800'>
                                  ⚠️ {Object.keys(invalidShips).length}{' '}
                                  {Object.keys(invalidShips).length === 1
                                    ? 'nave no válida'
                                    : 'naves no válidas'}
                                </div>
                              )}
                              {Object.keys(invalidShipTrips).length > 0 && (
                                <div className='text-sm text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-950 px-2 py-1 rounded border border-orange-200 dark:border-orange-800'>
                                  ⚠️ {Object.keys(invalidShipTrips).length}{' '}
                                  {Object.keys(invalidShipTrips).length === 1
                                    ? 'viaje no válido'
                                    : 'viajes no válidos'}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className='overflow-hidden'>
                            <div className='max-h-[520px] overflow-y-auto'>
                              <Table
                                aria-label='Resumen de liquidaciones'
                                className='min-w-full'
                                isCompact
                                removeWrapper
                                isHeaderSticky
                              >
                                <TableHeader>
                                  <TableColumn>EXISTE</TableColumn>
                                  <TableColumn>ERRORES</TableColumn>
                                  <TableColumn>REFERENCIA</TableColumn>
                                  <TableColumn>FECHA VENTA</TableColumn>
                                  <TableColumn>CLIENTE</TableColumn>
                                  <TableColumn>CLIENTE OFICIAL</TableColumn>
                                  <TableColumn>CONTENEDOR</TableColumn>
                                  <TableColumn>MERCADO VENTA</TableColumn>
                                  <TableColumn>MERCADO COMPARABLE</TableColumn>
                                  <TableColumn>COMISION</TableColumn>
                                  <TableColumn>IMPUESTOS</TableColumn>
                                  <TableColumn>REPACKING</TableColumn>
                                  <TableColumn>OTROS COSTOS</TableColumn>
                                  <TableColumn>FLETE USD</TableColumn>
                                  <TableColumn>MONEDA LOCAL</TableColumn>
                                  <TableColumn>INCOTERM</TableColumn>
                                  <TableColumn>TIPO CAMBIO</TableColumn>
                                  <TableColumn>TIPO TRANSPORTE</TableColumn>
                                  <TableColumn className='min-w-48'>
                                    NOMBRE NAVE
                                  </TableColumn>
                                  <TableColumn className='min-w-64'>
                                    VIAJE NAVE
                                  </TableColumn>
                                  <TableColumn>PUERTO DESTINO</TableColumn>
                                  <TableColumn>ETD</TableColumn>
                                  <TableColumn>ETA</TableColumn>
                                  <TableColumn>TOTAL INGRESOS</TableColumn>
                                  <TableColumn>TOTAL BULTOS</TableColumn>
                                  <TableColumn>TOTAL KILOS</TableColumn>
                                </TableHeader>
                                <TableBody>
                                  {processedData.generalCostos.map(
                                    (row, index) => {
                                      const totals = calculateSettlementTotals(
                                        row,
                                        processedData.ingresos
                                      );
                                      const reference = String(
                                        row['Número de referencia'] || ''
                                      );
                                      const validation =
                                        settlementValidationResults[reference];
                                      let status = 'Desconocida';
                                      let statusClass = 'text-default-400';
                                      if (validation) {
                                        if (!validation.exists) {
                                          status = 'Nueva';
                                          statusClass =
                                            'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950 font-bold';
                                        } else if (validation.isBlocked) {
                                          status = 'Bloqueada';
                                          statusClass =
                                            'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 font-bold';
                                        } else {
                                          status = 'Sobrescribir';
                                          statusClass =
                                            'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950 font-bold';
                                        }
                                      }
                                      return (
                                        <TableRow key={index}>
                                          <TableCell
                                            className={`font-medium p-2 rounded ${statusClass} text-center`}
                                          >
                                            {status}
                                          </TableCell>
                                          <TableCell className='text-center'>
                                            {(() => {
                                              const rowNumber = index + 2; // Excel rows start at 1, but we have header, so +2
                                              const reference = String(
                                                row['Número de referencia']
                                              )
                                                .trim()
                                                .toUpperCase();

                                              // Get errors for this row in General y Costos
                                              const generalErrors =
                                                processedData.validationErrors.filter(
                                                  (error: ValidationError) =>
                                                    error.sheet ===
                                                      'General y Costos' &&
                                                    error.row === rowNumber
                                                );

                                              // Get errors for this reference in Ingresos sheet
                                              const ingresosErrors =
                                                processedData.validationErrors.filter(
                                                  (error: ValidationError) => {
                                                    if (
                                                      error.sheet !== 'Ingresos'
                                                    )
                                                      return false;

                                                    // Find the ingresos row for this validation error
                                                    const ingresosRowIndex =
                                                      error.row - 2; // Convert to 0-based index
                                                    const ingresosRow =
                                                      processedData.ingresos[
                                                        ingresosRowIndex
                                                      ];

                                                    if (!ingresosRow)
                                                      return false;

                                                    // Check if this ingresos row belongs to the current reference
                                                    const ingresosReference =
                                                      String(
                                                        ingresosRow[
                                                          'Número de Referencia'
                                                        ] || ''
                                                      )
                                                        .trim()
                                                        .toUpperCase();
                                                    return (
                                                      ingresosReference ===
                                                      reference
                                                    );
                                                  }
                                                );

                                              const allErrors = [
                                                ...generalErrors,
                                                ...ingresosErrors,
                                              ];

                                              if (allErrors.length === 0) {
                                                return (
                                                  <span className='text-green-600 dark:text-green-400 font-medium'>
                                                    ✓
                                                  </span>
                                                );
                                              } else {
                                                return (
                                                  <Tooltip
                                                    content={
                                                      <div className='max-w-xs'>
                                                        {generalErrors.length >
                                                          0 && (
                                                          <div className='mb-2'>
                                                            <div className='font-semibold text-blue-200 mb-1'>
                                                              General y Costos:
                                                            </div>
                                                            {generalErrors.map(
                                                              (
                                                                error: ValidationError,
                                                                idx: number
                                                              ) => (
                                                                <div
                                                                  key={`general-${idx}`}
                                                                  className='mb-1 ml-2'
                                                                >
                                                                  <span className='font-semibold text-red-200'>
                                                                    {
                                                                      error.field
                                                                    }
                                                                    :
                                                                  </span>
                                                                  <span className='text-white ml-1'>
                                                                    {
                                                                      error.message
                                                                    }
                                                                  </span>
                                                                </div>
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                        {ingresosErrors.length >
                                                          0 && (
                                                          <div>
                                                            <div className='font-semibold text-green-200 mb-1'>
                                                              Ingresos:
                                                            </div>
                                                            {ingresosErrors.map(
                                                              (
                                                                error: ValidationError,
                                                                idx: number
                                                              ) => (
                                                                <div
                                                                  key={`ingresos-${idx}`}
                                                                  className='mb-1 ml-2'
                                                                >
                                                                  <span className='font-semibold text-red-200'>
                                                                    Fila{' '}
                                                                    {error.row}{' '}
                                                                    -{' '}
                                                                    {
                                                                      error.field
                                                                    }
                                                                    :
                                                                  </span>
                                                                  <span className='text-white ml-1'>
                                                                    {
                                                                      error.message
                                                                    }
                                                                  </span>
                                                                </div>
                                                              )
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    }
                                                    placement='top'
                                                    delay={0}
                                                    closeDelay={0}
                                                    showArrow={true}
                                                    color='danger'
                                                    className='max-w-xs'
                                                  >
                                                    <div className='text-red-600 dark:text-red-400 font-bold bg-red-50 dark:bg-red-950 px-2 py-1 rounded-full cursor-help hover:bg-red-100 transition-colors'>
                                                      {allErrors.length}
                                                    </div>
                                                  </Tooltip>
                                                );
                                              }
                                            })()}
                                          </TableCell>
                                          <TableCell className='font-mono text-sm'>
                                            {String(
                                              row['Número de referencia'] || ''
                                            )}
                                            {divisions.length > 1 &&
                                              (() => {
                                                const reference = String(
                                                  row['Número de referencia'] || ''
                                                )
                                                  .trim()
                                                  .toUpperCase();
                                                const divisionId =
                                                  settlementValidationResults[reference]?.divisionId;
                                                const division = divisions.find(
                                                  (d) => d.id === divisionId
                                                );
                                                return (
                                                  <div className='text-xs text-default-500'>
                                                    {division?.name ?? 'División pendiente'}
                                                  </div>
                                                );
                                              })()}
                                          </TableCell>
                                          <TableCell>
                                            {editingFechaVenta === index ? (
                                              <Input
                                                size='sm'
                                                type='date'
                                                value={
                                                  row['Fecha de venta']
                                                    ? new Date(
                                                        row['Fecha de venta']
                                                      )
                                                        .toISOString()
                                                        .split('T')[0]
                                                    : ''
                                                }
                                                onChange={(e) => {
                                                  updateRowAndClearErrors(
                                                    index,
                                                    {
                                                      'Fecha de venta':
                                                        e.target.value,
                                                    },
                                                    ['Fecha de venta']
                                                  );
                                                  // Don't close immediately - let user finish typing
                                                }}
                                                onBlur={() =>
                                                  setEditingFechaVenta(null)
                                                }
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    setEditingFechaVenta(null);
                                                  }
                                                  if (e.key === 'Escape') {
                                                    setEditingFechaVenta(null);
                                                  }
                                                }}
                                                placeholder='YYYY-MM-DD'
                                                className='min-w-32'
                                                autoFocus
                                              />
                                            ) : (
                                              <div
                                                className='cursor-pointer p-1 rounded border hover:bg-default-200 border-divider'
                                                onClick={() =>
                                                  setEditingFechaVenta(index)
                                                }
                                                title='Click para editar Fecha de Venta'
                                              >
                                                {row['Fecha de venta'] ? (
                                                  formatDate(
                                                    row['Fecha de venta'],
                                                    'dd MMM yyyy'
                                                  )
                                                ) : (
                                                  <span className='text-default-300'>
                                                    Click para editar
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {String(row['Cliente'] || '')}
                                          </TableCell>
                                          <TableCell>
                                            {(() => {
                                              if (
                                                hasUploadBlockingErrors(
                                                  row,
                                                  index
                                                )
                                              ) {
                                                return (
                                                  <span className='text-default-400'>
                                                    No se sube
                                                  </span>
                                                );
                                              }

                                              const resolution =
                                                getReceiverResolution(
                                                  String(row['Cliente'] || '')
                                                );

                                              if (!resolution.receiver) {
                                                return (
                                                  <span className='font-medium text-amber-700 dark:text-amber-300'>
                                                    Pendiente
                                                  </span>
                                                );
                                              }

                                              const sourceLabel =
                                                resolution.source ===
                                                'official'
                                                  ? 'Nombre oficial'
                                                  : resolution.source ===
                                                      'mapping'
                                                    ? 'Mapeo guardado'
                                                    : 'Mapeo de esta carga';

                                              return (
                                                <div>
                                                  <div className='font-medium'>
                                                    {resolution.receiver.name}
                                                  </div>
                                                  <div className='text-xs text-default-500'>
                                                    {sourceLabel}
                                                  </div>
                                                </div>
                                              );
                                            })()}
                                          </TableCell>
                                          <TableCell>
                                            {String(
                                              row['Número de Contenedor'] || ''
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {editingMarket === index ? (
                                              <Select
                                                size='sm'
                                                placeholder='Seleccionar mercado'
                                                selectedKeys={[
                                                  String(
                                                    row['Mercado Venta'] || ''
                                                  ),
                                                ]}
                                                onSelectionChange={(keys) => {
                                                  const selectedKey =
                                                    Array.from(
                                                      keys
                                                    )[0] as string;
                                                  if (
                                                    selectedKey &&
                                                    processedData
                                                  ) {
                                                    updateRowAndClearErrors(
                                                      index,
                                                      {
                                                        'Mercado Venta':
                                                          selectedKey,
                                                      },
                                                      ['Mercado Venta']
                                                    );
                                                    setEditingMarket(null);
                                                  }
                                                }}
                                                onClose={() =>
                                                  setEditingMarket(null)
                                                }
                                                className='min-w-40'
                                                autoFocus
                                              >
                                                {markets.map((market) => (
                                                  <SelectItem
                                                    key={market.name}
                                                    value={market.name}
                                                  >
                                                    {market.name} (
                                                    {market.country})
                                                  </SelectItem>
                                                ))}
                                              </Select>
                                            ) : (
                                              <div
                                                className={`cursor-pointer p-1 rounded ${
                                                  invalidMarkets[index]
                                                    ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-300'
                                                    : 'hover:bg-default-200'
                                                }`}
                                                onClick={() =>
                                                  setEditingMarket(index)
                                                }
                                                title={
                                                  invalidMarkets[index]
                                                    ? 'Mercado no válido - Click para editar'
                                                    : `Click para editar (${markets.length} mercados disponibles)`
                                                }
                                              >
                                                {String(
                                                  row['Mercado Venta'] || ''
                                                )}
                                                {invalidMarkets[index] && (
                                                  <span className='ml-2 text-red-600 dark:text-red-400'>
                                                    ⚠️
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {editingComparableMarket ===
                                            index ? (
                                              <Select
                                                size='sm'
                                                placeholder='Seleccionar mercado comparable'
                                                selectedKeys={[
                                                  String(
                                                    row['Mercado Comparable'] ||
                                                      ''
                                                  ),
                                                ]}
                                                onSelectionChange={(keys) => {
                                                  const selectedKey =
                                                    Array.from(
                                                      keys
                                                    )[0] as string;
                                                  if (processedData) {
                                                    updateRowAndClearErrors(
                                                      index,
                                                      {
                                                        'Mercado Comparable':
                                                          selectedKey,
                                                      },
                                                      ['Mercado Comparable']
                                                    );
                                                    setEditingComparableMarket(
                                                      null
                                                    );
                                                  }
                                                }}
                                                onClose={() =>
                                                  setEditingComparableMarket(
                                                    null
                                                  )
                                                }
                                                className='min-w-32'
                                                autoFocus
                                              >
                                                <SelectItem key='' value=''>
                                                  (Vacío)
                                                </SelectItem>
                                                <SelectItem
                                                  key='CNSHA'
                                                  value='CNSHA'
                                                >
                                                  CNSHA (Shanghai)
                                                </SelectItem>
                                                <SelectItem
                                                  key='CNCAN'
                                                  value='CNCAN'
                                                >
                                                  CNCAN (Guangzhou)
                                                </SelectItem>
                                              </Select>
                                            ) : (
                                              <div
                                                className={`cursor-pointer p-1 rounded ${
                                                  invalidComparableMarkets[
                                                    index
                                                  ]
                                                    ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-300'
                                                    : 'hover:bg-default-200'
                                                }`}
                                                onClick={() =>
                                                  setEditingComparableMarket(
                                                    index
                                                  )
                                                }
                                                title={
                                                  invalidComparableMarkets[
                                                    index
                                                  ]
                                                    ? 'Mercado comparable no válido - Click para editar'
                                                    : 'Click para editar (CNSHA, CNCAN o vacío)'
                                                }
                                              >
                                                {String(
                                                  row['Mercado Comparable'] ||
                                                    ''
                                                ) || '-'}
                                                {invalidComparableMarkets[
                                                  index
                                                ] && (
                                                  <span className='ml-2 text-red-600 dark:text-red-400'>
                                                    ⚠️
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {Number(
                                              row['Comisión (Moneda Local)'] ||
                                                0
                                            ).toLocaleString('es-ES', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}
                                          </TableCell>
                                          <TableCell>
                                            {Number(
                                              row['Impuestos (Moneda Local)'] ||
                                                0
                                            ).toLocaleString('es-ES', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}
                                          </TableCell>
                                          <TableCell>
                                            {Number(
                                              row[
                                                'Repacking (Moneda Local)'
                                              ] || 0
                                            ).toLocaleString('es-ES', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}
                                          </TableCell>
                                          <TableCell>
                                            {Number(
                                              row[
                                                'Otros Costos (Moneda Local)'
                                              ] || 0
                                            ).toLocaleString('es-ES', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}
                                          </TableCell>
                                          <TableCell>
                                            $
                                            {Number(
                                              row['Flete (USD)'] || 0
                                            ).toLocaleString('es-ES', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}
                                          </TableCell>
                                          <TableCell>
                                            {String(row['Moneda Local'] || '')}
                                          </TableCell>
                                          <TableCell>
                                            {String(
                                              row['Incoterm Original'] || ''
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {Number(
                                              row['Tipo de Cambio'] || 0
                                            ).toFixed(4)}
                                          </TableCell>
                                          <TableCell>
                                            <div className='flex flex-col space-y-2'>
                                              <TransportTypeSelector
                                                noTooltip={true}
                                                value={String(
                                                  row['Tipo Transporte'] || ''
                                                )}
                                                onChange={(newType) => {
                                                  if (processedData) {
                                                    const updatedData = {
                                                      ...processedData,
                                                    };
                                                    updatedData.generalCostos[
                                                      index
                                                    ]['Tipo Transporte'] =
                                                      newType;

                                                    if (newType === 'M') {
                                                      // Only auto-match if no trip is already selected
                                                      const currentShipTripId =
                                                        updatedData
                                                          .generalCostos[index][
                                                          '_shipTripId'
                                                        ];

                                                      console.log(
                                                        'Transport type change auto-matching check:',
                                                        {
                                                          index,
                                                          currentShipTripId,
                                                          willAutoMatch:
                                                            !currentShipTripId,
                                                        }
                                                      );

                                                      if (!currentShipTripId) {
                                                        // Try to auto-match ship and trip for Maritime transport
                                                        const shipName = String(
                                                          updatedData
                                                            .generalCostos[
                                                            index
                                                          ]['Nombre Nave'] || ''
                                                        ).trim();
                                                        const destinationPort =
                                                          String(
                                                            updatedData
                                                              .generalCostos[
                                                              index
                                                            ][
                                                              'Puerto de Destino'
                                                            ] || ''
                                                          ).trim();
                                                        const eta =
                                                          updatedData
                                                            .generalCostos[
                                                            index
                                                          ]['ETA'];
                                                        const fechaVenta =
                                                          updatedData
                                                            .generalCostos[
                                                            index
                                                          ]['Fecha de venta'];

                                                        if (shipName) {
                                                          // Find ship by name using regex
                                                          const matchedShip =
                                                            ships.find(
                                                              (ship) =>
                                                                new RegExp(
                                                                  ship.name.toLowerCase()
                                                                ).test(
                                                                  shipName.toLowerCase()
                                                                ) &&
                                                                ship.type ===
                                                                  'M'
                                                            );

                                                          if (matchedShip) {
                                                            updatedData.generalCostos[
                                                              index
                                                            ][
                                                              '_selectedShipId'
                                                            ] =
                                                              matchedShip.id.toString();
                                                            updatedData.generalCostos[
                                                              index
                                                            ]['Nombre Nave'] =
                                                              matchedShip.name;

                                                            // Try to find matching trip by destination port + ETA ±7 days
                                                            if (
                                                              destinationPort &&
                                                              eta
                                                            ) {
                                                              // Dates are now already converted to strings during validation
                                                              const etaDate =
                                                                new Date(eta);
                                                              const seasonDate =
                                                                fechaVenta
                                                                  ? new Date(
                                                                      fechaVenta
                                                                    )
                                                                  : eta;
                                                              const season =
                                                                calculateSeasonFromDate(
                                                                  seasonDate
                                                                );

                                                              const shipTripsForShip =
                                                                shipTrips.filter(
                                                                  (trip) =>
                                                                    trip.ship
                                                                      .id ===
                                                                      matchedShip.id &&
                                                                    calculateSeasonFromDate(
                                                                      trip.etd
                                                                    ) === season
                                                                );

                                                              let foundTrip =
                                                                null;
                                                              let bestTrip =
                                                                null; // For trips without destination match but with ETA match

                                                              console.log(
                                                                'Auto-matching trips for ship:',
                                                                matchedShip.name
                                                              );
                                                              console.log(
                                                                'Available trips:',
                                                                shipTripsForShip.map(
                                                                  (t) => ({
                                                                    id: t.id,
                                                                    eta: t.eta,
                                                                    market:
                                                                      t.market
                                                                        ?.name,
                                                                    etd: t.etd,
                                                                  })
                                                                )
                                                              );

                                                              for (const trip of shipTripsForShip) {
                                                                // Check if destination port matches (by name or ID)
                                                                const tripMarketName =
                                                                  trip.market?.name?.toUpperCase() ||
                                                                  '';
                                                                const tripMarketId =
                                                                  trip.market?.id?.toString() ||
                                                                  '';
                                                                const destinationUpper =
                                                                  destinationPort.toUpperCase();

                                                                const destinationMatches =
                                                                  tripMarketName &&
                                                                  tripMarketName !==
                                                                    '' &&
                                                                  (tripMarketName ===
                                                                    destinationUpper ||
                                                                    tripMarketId ===
                                                                      destinationPort ||
                                                                    tripMarketName.includes(
                                                                      destinationUpper
                                                                    ) ||
                                                                    destinationUpper.includes(
                                                                      tripMarketName
                                                                    ));

                                                                // Check ETA within ±7 days
                                                                const tripEtaDate =
                                                                  new Date(
                                                                    trip.eta
                                                                  );
                                                                const diffDays =
                                                                  Math.abs(
                                                                    (etaDate.getTime() -
                                                                      tripEtaDate.getTime()) /
                                                                      (1000 *
                                                                        60 *
                                                                        60 *
                                                                        24)
                                                                  );
                                                                const etaMatches =
                                                                  diffDays <= 7;

                                                                console.log(
                                                                  'Trip evaluation:',
                                                                  {
                                                                    tripId:
                                                                      trip.id,
                                                                    tripMarket:
                                                                      tripMarketName,
                                                                    destinationPort:
                                                                      destinationUpper,
                                                                    destinationMatches,
                                                                    etaMatches,
                                                                    diffDays,
                                                                    isSinDestino:
                                                                      tripMarketName ===
                                                                      'SIN DESTINO',
                                                                  }
                                                                );

                                                                if (
                                                                  destinationMatches &&
                                                                  etaMatches
                                                                ) {
                                                                  // Perfect match: destination + ETA
                                                                  console.log(
                                                                    'Perfect match found:',
                                                                    trip.id
                                                                  );
                                                                  foundTrip =
                                                                    trip;
                                                                  break;
                                                                } else if (
                                                                  etaMatches &&
                                                                  tripMarketName &&
                                                                  tripMarketName !==
                                                                    'SIN DESTINO'
                                                                ) {
                                                                  // Good match: ETA + has destination (not "Sin destino")
                                                                  console.log(
                                                                    'Good match candidate:',
                                                                    trip.id,
                                                                    tripMarketName
                                                                  );
                                                                  if (
                                                                    !bestTrip ||
                                                                    diffDays <
                                                                      Math.abs(
                                                                        (etaDate.getTime() -
                                                                          new Date(
                                                                            bestTrip.eta
                                                                          ).getTime()) /
                                                                          (1000 *
                                                                            60 *
                                                                            60 *
                                                                            24)
                                                                      )
                                                                  ) {
                                                                    bestTrip =
                                                                      trip;
                                                                    console.log(
                                                                      'New best trip:',
                                                                      trip.id
                                                                    );
                                                                  }
                                                                }
                                                              }

                                                              // Use best trip if no perfect match found
                                                              if (
                                                                !foundTrip &&
                                                                bestTrip
                                                              ) {
                                                                console.log(
                                                                  'Using best trip (no perfect match):',
                                                                  bestTrip.id
                                                                );
                                                                foundTrip =
                                                                  bestTrip;
                                                              }

                                                              if (foundTrip) {
                                                                console.log(
                                                                  'Final selected trip:',
                                                                  {
                                                                    id: foundTrip.id,
                                                                    market:
                                                                      foundTrip
                                                                        .market
                                                                        ?.name,
                                                                    eta: foundTrip.eta,
                                                                  }
                                                                );
                                                                updatedData.generalCostos[
                                                                  index
                                                                ][
                                                                  '_shipTripId'
                                                                ] =
                                                                  foundTrip.id;
                                                                updatedData.generalCostos[
                                                                  index
                                                                ][
                                                                  'Puerto de Destino'
                                                                ] =
                                                                  foundTrip
                                                                    .market
                                                                    ?.name ||
                                                                  '';
                                                                updatedData.generalCostos[
                                                                  index
                                                                ]['ETA'] =
                                                                  foundTrip.eta;
                                                              } else {
                                                                console.log(
                                                                  'No suitable trip found'
                                                                );
                                                              }
                                                            }
                                                          }
                                                        }
                                                      }
                                                    } else {
                                                      // Reset ship/trip related fields when transport type changes from M
                                                      delete updatedData
                                                        .generalCostos[index][
                                                        '_selectedShipId'
                                                      ];
                                                      delete updatedData
                                                        .generalCostos[index][
                                                        '_shipTripId'
                                                      ];
                                                    }

                                                    updateRowAndClearErrors(
                                                      index,
                                                      {
                                                        'Tipo Transporte':
                                                          newType,
                                                      },
                                                      ['Tipo Transporte']
                                                    );
                                                  }
                                                }}
                                              />
                                              {invalidTransports[index] && (
                                                <div className='text-xs text-red-600 dark:text-red-400 flex items-center'>
                                                  <span className='mr-1'>
                                                    ⚠️
                                                  </span>
                                                  Error en transporte
                                                </div>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            {row['Tipo Transporte'] === 'M' ? (
                                              editingShip === index ? (
                                                <Select
                                                  size='sm'
                                                  placeholder='Seleccionar nave'
                                                  selectedKeys={
                                                    row['_selectedShipId']
                                                      ? [row['_selectedShipId']]
                                                      : []
                                                  }
                                                  onSelectionChange={(keys) => {
                                                    const selectedKey =
                                                      Array.from(
                                                        keys
                                                      )[0] as string;
                                                    if (selectedKey) {
                                                      handleShipSelection(
                                                        index,
                                                        selectedKey
                                                      );
                                                    }
                                                  }}
                                                  onClose={() =>
                                                    setEditingShip(null)
                                                  }
                                                  className='min-w-52'
                                                  autoFocus
                                                >
                                                  {getMaritimeShips().map(
                                                    (ship) => (
                                                      <SelectItem
                                                        key={ship.id.toString()}
                                                        value={ship.id.toString()}
                                                      >
                                                        {ship.name}
                                                      </SelectItem>
                                                    )
                                                  )}
                                                </Select>
                                              ) : (
                                                <div
                                                  className='cursor-pointer p-1 rounded hover:bg-default-200 border border-divider'
                                                  onClick={() =>
                                                    setEditingShip(index)
                                                  }
                                                  title='Click para seleccionar nave'
                                                >
                                                  <div className='font-medium'>
                                                    {String(
                                                      row['Nombre Nave'] || ''
                                                    ) || 'Seleccionar nave'}
                                                  </div>
                                                </div>
                                              )
                                            ) : (
                                              <div className='p-1 bg-default-100 rounded border text-default-400 text-center'>
                                                <div className='text-xs'>
                                                  No necesario para
                                                  Aereo/Terrestre
                                                </div>
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {row['Tipo Transporte'] === 'M' &&
                                            row['_selectedShipId'] ? (
                                              editingTrip === index ? (
                                                <Select
                                                  size='sm'
                                                  placeholder='Seleccionar viaje'
                                                  selectedKeys={
                                                    row['_shipTripId']
                                                      ? [
                                                          row[
                                                            '_shipTripId'
                                                          ].toString(),
                                                        ]
                                                      : []
                                                  }
                                                  onSelectionChange={(keys) => {
                                                    const selectedKey =
                                                      Array.from(
                                                        keys
                                                      )[0] as string;
                                                    if (selectedKey) {
                                                      handleTripSelection(
                                                        index,
                                                        selectedKey
                                                      );
                                                    }
                                                  }}
                                                  onClose={() =>
                                                    setEditingTrip(null)
                                                  }
                                                  className='min-w-60'
                                                  autoFocus
                                                >
                                                  {getTripsByShip(
                                                    row['_selectedShipId']
                                                  ).map((trip) => (
                                                    <SelectItem
                                                      key={trip.id.toString()}
                                                      value={trip.id.toString()}
                                                    >
                                                      <div className='flex flex-col'>
                                                        <span className='text-xs'>
                                                          ETA:{' '}
                                                          {formatDate(
                                                            trip.eta,
                                                            'dd MMM yyyy'
                                                          )}{' '}
                                                          -{' '}
                                                          {trip.market?.name ||
                                                            'Sin destino'}
                                                        </span>
                                                      </div>
                                                    </SelectItem>
                                                  ))}
                                                </Select>
                                              ) : (
                                                <div
                                                  className='cursor-pointer p-1 rounded hover:bg-default-200 border border-divider'
                                                  onClick={() =>
                                                    setEditingTrip(index)
                                                  }
                                                  title='Click para seleccionar viaje'
                                                >
                                                  <div className='font-medium text-xs'>
                                                    {row['_shipTripId']
                                                      ? `ETA: ${(() => {
                                                          const trip =
                                                            shipTrips.find(
                                                              (t) =>
                                                                t.id ===
                                                                row[
                                                                  '_shipTripId'
                                                                ]
                                                            );
                                                          return trip
                                                            ? formatDate(
                                                                trip.eta,
                                                                'dd MMM yyyy'
                                                              )
                                                            : 'N/A';
                                                        })()} - ${String(row['Puerto de Destino'] || '')}`
                                                      : 'Seleccionar viaje'}
                                                  </div>
                                                </div>
                                              )
                                            ) : row['Tipo Transporte'] ===
                                              'M' ? (
                                              <div className='p-1 bg-default-100 rounded border text-default-400 text-center'>
                                                <div className='text-xs'>
                                                  Selecciona nave primero
                                                </div>
                                              </div>
                                            ) : (
                                              <div className='p-1 bg-default-100 rounded border text-default-400 text-center'>
                                                <div className='text-xs'>
                                                  No necesario para
                                                  Aereo/Terrestre
                                                </div>
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {row['Tipo Transporte'] === 'M' ? (
                                              // Read-only for Maritime
                                              <div className='p-1 bg-default-100 rounded border text-center'>
                                                <div className='text-xs text-default-400'>
                                                  No necesario para Marítimo
                                                </div>
                                              </div>
                                            ) : // Editable for A/T types
                                            editingDestinationPort === index ? (
                                              <Select
                                                size='sm'
                                                placeholder='Seleccionar puerto'
                                                selectedKeys={[
                                                  String(
                                                    row['Puerto de Destino'] ||
                                                      ''
                                                  ),
                                                ]}
                                                onSelectionChange={(keys) => {
                                                  const selectedKey =
                                                    Array.from(
                                                      keys
                                                    )[0] as string;
                                                  if (
                                                    selectedKey &&
                                                    processedData
                                                  ) {
                                                    updateRowAndClearErrors(
                                                      index,
                                                      {
                                                        'Puerto de Destino':
                                                          selectedKey,
                                                      },
                                                      ['Puerto de Destino']
                                                    );
                                                    setEditingDestinationPort(
                                                      null
                                                    );
                                                  }
                                                }}
                                                onClose={() =>
                                                  setEditingDestinationPort(
                                                    null
                                                  )
                                                }
                                                className='min-w-40'
                                                autoFocus
                                              >
                                                {markets.map((market) => (
                                                  <SelectItem
                                                    key={market.name}
                                                    value={market.name}
                                                  >
                                                    {market.name} (
                                                    {market.country})
                                                  </SelectItem>
                                                ))}
                                              </Select>
                                            ) : (
                                              <div
                                                className={`cursor-pointer p-1 rounded border ${
                                                  invalidDestinationPorts[index]
                                                    ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-800 dark:text-red-300'
                                                    : 'hover:bg-default-200 border-divider'
                                                }`}
                                                onClick={() =>
                                                  setEditingDestinationPort(
                                                    index
                                                  )
                                                }
                                                title={
                                                  invalidDestinationPorts[index]
                                                    ? 'Puerto de destino no válido - Click para editar'
                                                    : `Click para editar (${markets.length} puertos disponibles)`
                                                }
                                              >
                                                {String(
                                                  row['Puerto de Destino'] || ''
                                                ) || 'Seleccionar puerto'}
                                                {invalidDestinationPorts[
                                                  index
                                                ] && (
                                                  <span className='ml-2 text-red-600 dark:text-red-400'>
                                                    ⚠️
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {row['Tipo Transporte'] === 'M' ? (
                                              // Read-only for Maritime
                                              <div className='p-1 bg-default-100 rounded border text-center'>
                                                <div className='text-xs text-default-400'>
                                                  No necesario para Marítimo
                                                </div>
                                              </div>
                                            ) : // Interactive date input for A/T
                                            editingETD === index ? (
                                              <Input
                                                size='sm'
                                                type='date'
                                                value={
                                                  row['ETD']
                                                    ? new Date(row['ETD'])
                                                        .toISOString()
                                                        .split('T')[0]
                                                    : ''
                                                }
                                                onChange={(e) => {
                                                  updateRowAndClearErrors(
                                                    index,
                                                    {
                                                      ETD: e.target.value,
                                                    },
                                                    ['ETD']
                                                  ); // Clear date and trip errors
                                                  // Don't close immediately - let user finish typing
                                                }}
                                                onBlur={() =>
                                                  setEditingETD(null)
                                                }
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    setEditingETD(null);
                                                  }
                                                  if (e.key === 'Escape') {
                                                    setEditingETD(null);
                                                  }
                                                }}
                                                placeholder='YYYY-MM-DD'
                                                className='min-w-32'
                                                autoFocus
                                              />
                                            ) : (
                                              <div
                                                className='cursor-pointer p-1 rounded border hover:bg-default-200 border-divider'
                                                onClick={() =>
                                                  setEditingETD(index)
                                                }
                                                title='Click para editar ETD'
                                              >
                                                {row['ETD'] ? (
                                                  formatDate(
                                                    row['ETD'],
                                                    'dd MMM yyyy'
                                                  )
                                                ) : (
                                                  <span className='text-default-300'>
                                                    Click para editar
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {row['Tipo Transporte'] === 'M' ? (
                                              // Read-only for Maritime
                                              <div className='p-1 bg-default-100 rounded border text-center'>
                                                <div className='text-xs text-default-400'>
                                                  No necesario para Marítimo
                                                </div>
                                              </div>
                                            ) : // Interactive date input for A/T
                                            editingETA === index ? (
                                              <Input
                                                size='sm'
                                                type='date'
                                                value={
                                                  row['ETA']
                                                    ? new Date(row['ETA'])
                                                        .toISOString()
                                                        .split('T')[0]
                                                    : ''
                                                }
                                                onChange={(e) => {
                                                  updateRowAndClearErrors(
                                                    index,
                                                    {
                                                      ETA: e.target.value,
                                                    },
                                                    ['ETA']
                                                  ); // Clear date and trip errors
                                                  // Don't close immediately - let user finish typing
                                                }}
                                                onBlur={() =>
                                                  setEditingETA(null)
                                                }
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    setEditingETA(null);
                                                  }
                                                  if (e.key === 'Escape') {
                                                    setEditingETA(null);
                                                  }
                                                }}
                                                placeholder='YYYY-MM-DD'
                                                className='min-w-32'
                                                autoFocus
                                              />
                                            ) : (
                                              <div
                                                className='cursor-pointer p-1 rounded border hover:bg-default-200 border-divider'
                                                onClick={() =>
                                                  setEditingETA(index)
                                                }
                                                title='Click para editar ETA'
                                              >
                                                {row['ETA'] ? (
                                                  formatDate(
                                                    row['ETA'],
                                                    'dd MMM yyyy'
                                                  )
                                                ) : (
                                                  <span className='text-default-300'>
                                                    Click para editar
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell className='font-semibold text-green-700 dark:text-green-300'>
                                            $
                                            {totals.totalIngresos.toLocaleString(
                                              'es-ES',
                                              {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              }
                                            )}
                                          </TableCell>
                                          <TableCell className='font-semibold text-blue-700 dark:text-blue-300'>
                                            {totals.totalBultos.toLocaleString(
                                              'es-ES'
                                            )}
                                          </TableCell>
                                          <TableCell className='font-semibold text-orange-700 dark:text-orange-300'>
                                            {totals.totalKilos.toLocaleString(
                                              'es-ES',
                                              {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              }
                                            )}{' '}
                                            kg
                                          </TableCell>
                                        </TableRow>
                                      );
                                    }
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        </div>

                        {/* Success message */}
                        {!processedData.summary.hasErrors &&
                          !isLoadingReceivers &&
                          unresolvedReceiverGroups.length === 0 && (
                          <div className='bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4'>
                            <div className='flex items-center space-x-2'>
                              <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                              <p className='text-sm text-green-800 dark:text-green-200'>
                                <strong>¡Perfecto!</strong> Todos los datos han
                                sido validados correctamente y están listos para
                                procesar.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </ModalBody>
          {isUploading && step === 'review' && uploadProgress.total > 0 && (
            <div className='px-6 pb-2'>
              <div className='space-y-2'>
                <p className='text-sm text-default-500'>
                  {uploadStage === 'comparison'
                    ? 'Calculando comparación de precios...'
                    : 'Subiendo liquidaciones...'}{' '}
                  {uploadProgress.processed}/{uploadProgress.total}
                </p>
                <Progress
                  value={uploadProgressPercent}
                  size='sm'
                  color='primary'
                />
              </div>
            </div>
          )}
          <ModalFooter>
            <Button
              variant='light'
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancelar
            </Button>
            {step !== 'upload' && (
              <Button
                isDisabled={isUploading}
                variant='flat'
                onClick={handleBackToUpload}
                startContent={<Icon icon='tabler:arrow-left' />}
              >
                Volver a Subir
              </Button>
            )}
            {step === 'upload' ? (
              <Button
                color='primary'
                onClick={handleUpload}
                disabled={
                  isUploading ||
                  !selectedFile ||
                  isUploading ||
                  !season ||
                  !selectedSpecies
                }
                startContent={<Icon icon='tabler:upload' />}
              >
                {isUploading
                  ? 'Procesando...'
                  : isValidatingSettlements
                    ? 'Validando liquidaciones...'
                    : 'Procesar Archivo'}
              </Button>
            ) : (
              <Button
                color='primary'
                onClick={handleMassiveUpload}
                isDisabled={
                  isUploading ||
                  !processedData ||
                  processedData.generalCostos.length === 0 ||
                  isLoadingReceivers ||
                  unresolvedReceiverGroups.length > 0
                }
                startContent={<Icon icon='tabler:upload' />}
              >
                {isUploading
                  ? 'Subiendo... esto puede demorar unos minutos'
                  : `Subir Liquidaciones${currentInvalidReferences.length > 0 ? ` (excepto ${currentInvalidReferences.length} no válidas)` : ''}`}{' '}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={Boolean(createReceiverGroup)}
        onClose={closeCreateReceiverModal}
        size='md'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            Crear Nuevo Cliente
            {createReceiverGroup && (
              <p className='text-sm font-normal text-default-500'>
                Se agregará a Configuración, sección Clientes.
              </p>
            )}
          </ModalHeader>
          <ModalBody>
            <Input
              label='Nombre del cliente'
              value={createReceiverName}
              onChange={(event) => setCreateReceiverName(event.target.value)}
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={closeCreateReceiverModal}>
              Cancelar
            </Button>
            <Button
              color='primary'
              isLoading={isCreatingReceiver}
              onPress={handleCreateReceiver}
            >
              Crear cliente
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Instructions Modal */}
      <Modal
        isOpen={isInstructionsModalOpen}
        onClose={() => setIsInstructionsModalOpen(false)}
        size='3xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            <h3 className='text-lg font-bold'>📋 Instrucciones de Llenado</h3>
            <p className='text-xs text-default-500'>
              Guía completa para llenar correctamente el archivo Excel
            </p>
          </ModalHeader>
          <ModalBody className='max-h-[70vh] overflow-y-auto'>
            <div className='space-y-4'>
              {/* General y Costos Instructions */}
              <div className='bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3'>
                <h4 className='font-semibold text-blue-900 dark:text-blue-200 mb-2 text-sm'>
                  🏢 Hoja "General y Costos"
                </h4>
                <div className='space-y-2 text-xs text-blue-800 dark:text-blue-300'>
                  <div>
                    <p className='font-medium text-blue-900 dark:text-blue-200'>
                      📋 Obligatorios:
                    </p>
                    <p className='ml-2'>
                      • Número de referencia, Fecha de venta, Cliente,
                      Contenedor, Mercado Venta, Puerto de Destino, Moneda Local
                      (identificador de 3 letras como RMB, HKD, USD, etc.),
                      Incoterm (FOB, CIF, etc.), Tipo de Cambio
                    </p>
                  </div>

                  <div>
                    <p className='font-medium text-blue-900 dark:text-blue-200'>⚙️ Opcionales:</p>
                    <p className='ml-2'>
                      • Mercado Comparable (CNSHA si es Shanghai, CNCAN si es
                      Guangzhou, vacío si no quieres compararlo), Comisión
                      (Moneda Local), Impuestos (Moneda Local), Otros Costos
                      (Moneda Local), Repacking (Moneda Local), Flete (USD),
                      Tipo de Negocio, Orden de Venta
                    </p>
                  </div>

                  <div>
                    <p className='font-medium text-blue-900 dark:text-blue-200'>🚚 Transporte:</p>
                    <p className='ml-2'>
                      • Tipo de Transporte: A (aéreo), T (terrestre) o M
                      (marítimo)
                    </p>
                    <p className='ml-2'>
                      • Marítimo (M): auto-detecta nave por nombre y viaje por
                      Puerto Destino + ETA (±7 días)
                    </p>
                    <p className='ml-2'>
                      • Aéreo o Terrestre (A o T): completa manualmente Puerto
                      Destino, ETD y ETA
                    </p>
                  </div>
                </div>
              </div>

              {/* Ingresos Instructions */}
              <div className='bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3'>
                <h4 className='font-semibold text-green-900 dark:text-green-200 mb-2 text-sm'>
                  📦 Hoja "Ingresos"
                </h4>
                <div className='space-y-2 text-xs text-green-800 dark:text-green-300'>
                  <div>
                    <p className='font-medium text-green-900 dark:text-green-200'>
                      📋 Obligatorios:
                    </p>
                    <p className='ml-2'>
                      • Número de Referencia (debe existir en General y Costos)
                    </p>
                    <p className='ml-2'>
                      • Variedad, Calibre, Etiqueta, Categoría
                    </p>
                    <p className='ml-2'>
                      • Formato: CAJA, 2IN1, 3IN1, 4IN1, 5IN1, 6IN1, etc.
                    </p>
                    <p className='ml-2'>
                      • Peso Bulto &gt; 0, Cantidad de Bultos &gt; 0
                    </p>
                  </div>

                  <div>
                    <p className='font-medium text-green-900 dark:text-green-200'>
                      🍒 Para Cerezas:
                    </p>
                    <p className='ml-2'>
                      • Calibre: L, XL, J, 2J, 3J, 4J, 5J, GIA, MIX
                    </p>
                    <p className='ml-2'>• Color: L, D, DD</p>
                  </div>

                  <div>
                    <p className='font-medium text-green-900 dark:text-green-200'>⚙️ Opcionales:</p>
                    <p className='ml-2'>• Pallet (puede estar vacío)</p>
                    <p className='ml-2'>
                      • Precio Bulto (Moneda Local) (puede ser 0)
                    </p>
                  </div>
                </div>
              </div>

              {/* Important Notes */}
              <div className='bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3'>
                <h4 className='font-semibold text-yellow-900 dark:text-yellow-200 mb-2 text-sm'>
                  ⚠️ Notas Importantes
                </h4>
                <div className='space-y-1 text-xs text-yellow-800 dark:text-yellow-300'>
                  <p>
                    • <strong>Vinculación:</strong> Cada liquidación debe tener
                    detalles de ingresos
                  </p>
                  <p>
                    • <strong>Referencias:</strong> Usar el mismo número para
                    vincular hojas (sin duplicados)
                  </p>
                  <p>
                    • <strong>Formato:</strong> Textos se convierten
                    automáticamente a mayúsculas
                  </p>
                  <p>
                    • <strong>Mercados:</strong> Mercado Venta y Puerto de
                    Destino deben existir en el sistema
                  </p>
                  <p>
                    • <strong>Fechas:</strong> DD/MM/YYYY o YYYY-MM-DD
                  </p>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color='primary'
              onClick={() => setIsInstructionsModalOpen(false)}
              size='sm'
            >
              Entendido
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ExcelMassiveUploadModal;
